import { isPartnerStatus } from "./redesign/contactTabs";
import * as React from "react";
import { plzPruefen, plzSperrtSpeichern } from "./forms/validation/laenderprofil";
import type { BankTreffer } from "./forms/widgets/bankleitzahlen";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { REFINED } from "./risikoanalyseStep/tokens";
import {
  SectionCard,
  FieldRow,
  LabeledInput,
  LabeledTextarea,
  LabeledSelect,
  TagInput,
  useDuplicateCheck,
  DuplicateWarningPanel,
  RequiredFieldsProgress,
  StatusSegmented,
  NationalitySelect,
  PhoneInput,
  EmailInput,
  IbanInput,
  BicInput,
  AddressAutocomplete,
  KartenKnopf,
  CityAutocomplete,
  CommFirmaWahl,
  commFirmaText,
  CountrySelect,
  SocialMediaInput,
  PLACEHOLDERS,
  validatePhone,
  validateEmail,
  validateIban,
  validateBic,
  useFormPrimitiveStyles,
} from "./forms";
import { documentService } from "../services/documentService";
import { HerkunftAbschnitt } from "./herkunft/HerkunftAbschnitt";
import { herkunftVerknuepfen } from "./herkunft/herkunftBezug";
import { useContactOrigins } from "./useContactOrigins";
import { PrivateRelationsSection } from "./relationsInline/PrivateRelationsSection";
import { ArbeitgeberAuswahl, ArbeitgeberSchnellanlage } from "./relationsInline/ArbeitgeberAuswahl";
import { FirmendatenBefuellenDialog } from "./FirmendatenBefuellenDialog";
import { kontoinhaberVorschlag } from "../utils/kontoinhaber";
import { gemerktesLogo, logoMerken, logoVergessen } from "../utils/logoVorschlag";
import { arbeitgeberSetzen } from "../services/arbeitgeberService";
import {
  VereinMitgliedschaften,
  type VereinEintrag,
} from "./forms/widgets/VereinMitgliedschaften";
import { arbeitgeberEintragNachreichen, createContactFromRef } from "./relationsInline/persistRelations";
import { arbeitgeberSpiegeln } from "../utils/arbeitgeberVerbindung";
import { firmaInVerbindungen } from "../utils/visitenkarteVerbindung";
import { ehepartnerInHaushalt } from "../utils/urkundenZuFormState";
import { firmenbezugNachreichen } from "../utils/firmenbezugNachreichen";
import { refHasName, type ContactRef } from "./relationsInline/types";
import type { AnlageNachlaufRef } from "../utils/anlageNachlauf";
import { STEUERKLASSEN } from "../utils/steuerUndAusweisAngaben";
import { useAuth } from "../context/AuthContext";
import { useAnredeOptionen } from "./forms/useAnredeOptionen";
import { IconVisitenkarte } from "./forms/formPrimitives";
import DateiScannenDialog, {
  type DateiScanUebernahme,
} from "./DateiScannenDialog";
import { DEFAULT_NATIONALITY } from "../data/nationalities";
import { SELBSTSTAENDIGKEIT_OPTIONEN } from "../data/selbststaendigkeit";
import {
  RENTNER_STATUS,
  WEITERE_TAETIGKEIT_OPTIONEN,
  WEITERE_TAETIGKEIT_STANDARD,
  berufFelderZuLeeren,
  berufSichtbarkeit,
  istRentner,
} from "../data/berufRentner";
import {
  AusweisdokumentAbschnitt,
  type AusweisdokumentEintrag,
} from "./forms/widgets/AusweisdokumentAbschnitt";
import { useDokumentDateien } from "./forms/useDokumentDateien";
import { dokumentScanVerteilen, visitenkarteFirmenbezug, DOKUMENT_SCHLUESSEL } from "../utils/dokumentScanUebernahme";
import { STATUS_AP_PRODUKTPARTNER } from "../data/produktpartnerKatalog";
import {
  STATUS_EINTRAEGE_PERSON,
  haushaltsAngabeVon,
} from "../data/statusAuswahlKatalog";
import { bezuegeAusFeld } from "./forms/widgets/StatusBezuege";
import {
  STATUS_SONSTIGE,
  rollenAusFeld,
} from "../data/kontaktstatusSegment";
import { KontaktStatusSchritt } from "./forms/KontaktStatusSchritt";
import { PersonenMitBeziehungInline, UnternehmenInline } from "./relationsInline/StatusFolgefelder";
import { ZuwendungenKarte } from "./forms/ZuwendungenKarte";
import {
  VertretungVollmachtenAbschnitt,
  type VerfuegungStateItem,
  type VertretungStateItem,
} from "./forms/widgets/VertretungVollmachtenAbschnitt";
import {
  WeitereKommunikationDialog,
  type WeitereKommunikationEintrag,
} from "./forms/widgets/WeitereKommunikationDialog";
import {
  ErreichbarkeitModal,
  type ErreichbarkeitRufnummerItem,
} from "./forms/widgets/ErreichbarkeitModal";

import { EINLADUNGS_KENNZEICHEN } from "../data/zuwendungsarten";
import { VerfuegungenListe } from "./redesign/VertretungUndVollmacht";
import type { VerfuegungForm } from "./redesign/vertretungLogik";
import { VertretungList } from "./relationsInline/VertretungList";
// Die Verfügungsliste bringt ihre Optik aus dem Akten-Stylesheet mit
// (`.vtr-*`). Es wird pro Komponente importiert — dasselbe Muster wie in
// VorgaengeMo, DamagesTable und KontaktAuswahlPopup.
import "./redesign/contactRedesign.css";
import {
  KommHinweise,
  andererKontaktKommHinweis,
  doppeltKommHinweis,
  erreichbarkeitUmhaengen,
  telefonMobilKommHinweis,
  useTelefonMobilPaar,
} from "./forms/kommunikation/TelefonMobilHinweis";
import { doppelteWege, weitereKommWege, type KontaktWeg, type WegTyp } from "./forms/kommunikation/doppelteWege";
import { useNummernAndererKontakte } from "./forms/kommunikation/useNummernAndererKontakte";
import { ContactTodesfallBelege } from "./redesign/ContactTodesfallBelege";
import { todesfallBelegeAusFeld } from "../data/todesfallBelege";
import { privateRequiredChecks } from "./newPrivateRequired";
import { AnlageSpeichernLeiste, type AnlageMangel } from "./forms/AnlageSpeichernLeiste";
import {
  OffeneVollanlagenContext,
  VollanlageElternContext,
  speichernWegenVollanlageBlockiert,
  useOffeneVollanlagenRegister,
  type VollanlageEltern,
} from "./forms/VollanlageWeiter";
import { NkgKarte } from "./forms/NkgKarte";
import { createNkgVorgang } from "../services/menschenFuersUnternehmenService";
import { AnsprechpartnerFelder } from "./produktpartner/AnsprechpartnerFelder";
import { validatePartnerProfile } from "./produktpartner/partnerProfileModel";
import {
  ownershipAusFormular,
  persistCollectedRelations,
  syncCollectedRelations,
  assignKundenverbindung,
  describeRemovedRelations,
} from "./relationsInline/persistRelations";
import { ConfirmDialog } from "./redesign/ConfirmDialog";
import { KOMM_SPALTEN, KOMM_SPALTEN_TITEL } from "./redesign/kommunikationSpaltenPlan";
import OwnershipSection from "./forms/OwnershipSection";
import { EintragsListe } from "./forms/EintragsListe";
import {
  VollmachtenKarte,
  anschriftEinzeilig,
  standardVollmachtAuswahl,
  vollmachtenNachAnlageMelden,
  type VollmachtAuswahl,
} from "./vollmachten/VollmachtenKarte";
import { haushaltAusFormular, verbundAusFormular } from "./vollmachten/erstinfoSammel";
import { wegFormatOk, wegeLesen } from "./forms/kommunikation/weitereWege";
import { KartenKlappContext, useKartenKlapp } from "./forms/kartenKlapp";
import { PRIVAT_KARTEN, leereKarten } from "./forms/kartenLeer";
import {
  WeitereAnschriftFelder,
  WeitereBankFelder,
  anschriftIstLeer,
  anschriftPruefen,
  bankIstLeer,
  bankPruefen,
  neueAnschrift,
  neueBank,
  type WeitereAnschrift,
  type WeitereBank,
} from "./forms/weitereEintraegeFelder";
import { anschriftKurz, anzahlKurz, bankKurz, kommunikationKurz } from "./forms/kartenKurzfassung";
import { ConvertBanner } from "./ConvertBanner";
import type { HochzeitsDateiItem } from "./relationsInline/FamilienstandSection";
import { apiUrl } from "../utils/api";
import {
  deriveBerufFromConnections,
  type BerufConnection,
} from "../utils/deriveBerufFromConnections";
import type { PersonFirmaItem, AssistantItem, HouseholdItem } from "./relationsInline/types";
import {
  geschaeftlicheFirmen,
  gespeicherteFirmen,
  gleicheFirma,
  slotFirmen,
  waehlbareFirmen,
  weitereFirmen,
  type CommCompanies,
  type FirmaOption,
} from "../utils/geschaeftlichFirma";
import { firmenVoll } from "../utils/firmenAnzeige";
import type { FormState } from "../utils/formUtils";
import { usePersonZentrale } from "./forms/widgets/firmenZentrale";
import { ortsteilLeeren } from "../utils/ortsteilAbgleich";
import { hinweisen } from "./redesign/bestaetigen";
import { elementMitId } from "./windowManager/browserFenster/fensterRegister";

/**
 * Saubere, neu aufgebaute Privatkontakt-Anlage auf C0/BRAND-Bausteinen.
 * Ersetzt die alte, verworrene SinglePageForm im Create-Flow. Bindet an
 * dieselben formFields-Keys (Persistenz/RBAC unverändert) und reicht den
 * Save-Flow an die Page durch (onSave = build payload + POST → id).
 */

const useStyles = makeStyles({
  page: {
    minHeight: "100%",
    backgroundColor: REFINED.white,
    fontFamily: REFINED.fontUI,
    color: REFINED.ink,
    display: "flex",
    flexDirection: "column",
  },
  scroll: { flex: 1, overflowY: "auto" },
  inner: {
    // Volle Breite (User 30.09.2026): die Karten füllen den verfügbaren Platz.
    // Ohne border-box käme der Innenabstand zur Breite dazu und die Seite
    // ließe sich seitlich schieben (User 26.09.2026).
    width: "100%",
    boxSizing: "border-box",
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    rowGap: "18px",
  },
  // In der Akte eingebettet: die Akte (`.dbody`) bringt schon 32 px Rand mit —
  // seitlich kein zweiter Innenabstand.
  innerEingebettet: { paddingLeft: 0, paddingRight: 0 },
  pageTitle: { fontFamily: REFINED.fontUI, fontSize: "1.7rem", fontWeight: 700, color: REFINED.ink, textAlign: "center", margin: "12px 0 24px" },
  rowActions: { display: "flex", justifyContent: "flex-end", marginTop: "2px" },
  // Kommunikation: eine Spalte („Privat" bzw. „Geschäftlich") samt Überschrift.
  commCol: { display: "flex", flexDirection: "column", rowGap: "16px", minWidth: 0 },
  commColHead: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.085em",
    textTransform: "uppercase",
    color: REFINED.aub,
    paddingBottom: "6px",
    ...shorthands.borderBottom("1px", "solid", REFINED.line),
  },
  ghostBtn: {
    ...shorthands.border("1.5px", "solid", REFINED.aub),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.white,
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.88rem",
    padding: "9px 16px",
    cursor: "pointer",
    transitionProperty: "background-color, color",
    transitionDuration: "140ms",
    ":hover": { backgroundColor: REFINED.aub50 },
  },
  // Sichtbarkeits-Hinweis: rahmenlos und ruhig (so will es der PO), aber in
  // Erklärende Zeile unter einem Feld (z. B. „kommt aus der Firmenakte").
  hintText: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.82rem",
    lineHeight: 1.45,
    color: REFINED.ink3,
  },
  removeBtn: {
    ...shorthands.border("1px", "solid", REFINED.err),
    ...shorthands.borderRadius("8px"),
    backgroundColor: REFINED.white,
    color: REFINED.err,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    cursor: "pointer",
    fontSize: "0.82rem",
    padding: "6px 12px",
    ":hover": { backgroundColor: REFINED.errBg },
  },
  fileBtn: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    padding: "10px 14px",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.white,
    color: REFINED.ink,
    fontFamily: REFINED.fontUI,
    fontSize: "0.88rem",
    cursor: "pointer",
    transitionProperty: "border-color, color",
    transitionDuration: "140ms",
    ":hover": { ...shorthands.borderColor(REFINED.aub), color: REFINED.aub },
  },
  footerBar: {
    position: "sticky",
    bottom: 0,
    backgroundColor: REFINED.white,
    ...shorthands.borderTop("1px", "solid", REFINED.line),
    display: "flex",
    flexDirection: "column",
  },
  footer: {
    padding: "14px 22px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    columnGap: "12px",
  },
  cancelBtn: {
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.white,
    color: REFINED.ink,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.9rem",
    padding: "10px 20px",
    cursor: "pointer",
    transitionProperty: "border-color, color",
    transitionDuration: "140ms",
    ":hover": { ...shorthands.borderColor(REFINED.aub), color: REFINED.aub },
  },
  saveBtn: {
    ...shorthands.borderWidth("0"),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.aub,
    color: REFINED.white,
    fontFamily: REFINED.fontUI,
    fontSize: "0.9rem",
    fontWeight: 600,
    padding: "10px 24px",
    cursor: "pointer",
    boxShadow: REFINED.btnShadow,
    transitionProperty: "background-color, box-shadow",
    transitionDuration: "140ms",
    ":hover": { backgroundColor: REFINED.aub700 },
    ":disabled": { opacity: 0.5, cursor: "default", boxShadow: "none" },
  },
  footerRight: { display: "flex", alignItems: "center", columnGap: "12px" },
  /** „Datei scannen" — sekundaer, gleiche Optik wie „Daten befuellen"
   *  in der Firmen-Anlage. */
  scanBtn: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "7px",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.white,
    color: REFINED.ink,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.9rem",
    padding: "10px 18px",
    cursor: "pointer",
    transitionProperty: "border-color, color",
    transitionDuration: "140ms",
    ":hover": { ...shorthands.borderColor(REFINED.aub), color: REFINED.aub },
  },
  reqHint: { fontFamily: REFINED.fontUI, fontSize: "0.82rem", color: REFINED.err, fontWeight: 600 },
});

type Props = {
  formFields: FormState;
  updateField: (name: string, value: string) => void;
  /** Page-Handler: baut Payload, POSTet /contact, liefert die neue id (oder false). */
  onSave: () => Promise<number | false>;
  /** Nach erfolgreichem Speichern + Foto/Relationen-Upload. */
  onSaveSuccess: (contactId: number) => void;
  onClose: () => void;
  /**
   * Umwandlungs-Modus: dasselbe Formular, aber vorausgefüllt aus einem
   * BESTEHENDEN Kontakt (Firma → Person). Ändert nur Rahmen und Verhalten am
   * Rand — die Felder und ihre Validierung bleiben identisch zur Anlage.
   */
  convert?: {
    /** Bisheriger Typ des Kontakts (für die Erklärung im Banner). */
    from: "Firma" | "Person";
    /** Beziehungen, die beim Umwandeln ihre Rolle verloren haben. */
    relationsWithoutRole?: { name: string; previous: string }[];
    /** Felder des bisherigen Typs, die geleert werden. */
    fieldsToClear?: { label: string; value: string }[];
    /** Id des umzuwandelnden Kontakts — „Aus KMVS wählen" im Scan startet mit seiner Akte (T-41). */
    contactId?: number;
  };
  /**
   * Einstieg über „Neuer Kontakt": Wechsel auf die Firmenanlage. Fehlt er, ist
   * der Typ fest (Umwandeln, Fenster-Modus).
   */
  onTypWechsel?: (typ: "firma") => void;
  /**
   * Bearbeiten-Modus der Kontaktakte (Reiter „Allgemein" = Anlage, User
   * 27.09.2026): vorbefüllt aus einem bestehenden Kontakt. Status-Schritt, NKG,
   * Vollmachten, Verantwortlichkeit und Foto stehen dann im Kopf der Akte und
   * entfallen hier. Speichern übernimmt allein `onSave` des Aufrufers
   * (Diff-PUT + Beziehungsabgleich) — die Nachläufe der Anlage laufen nicht.
   */
  bearbeiten?: { contactId: number; nurLesen?: boolean; gesperrt?: boolean; nachlauf?: AnlageNachlaufRef };
  /**
   * Beschriftung des Speichern-Knopfs — „Übernehmen"/„Weiter", wenn die
   * Anlage aus einer Schnellanlage geöffnet wurde (User 02.10.2026).
   */
  speichernText?: string;
};

const TITLE_OPTIONS = [
  { value: "Dr.", label: "Dr." },
  { value: "Prof.", label: "Prof." },
  { value: "Prof. Dr.", label: "Prof. Dr." },
];

const str = (v: unknown): string => (typeof v === "string" ? v : "");
const parseArr = (raw: unknown): any[] => {
  if (typeof raw !== "string" || !raw.trim()) return [];
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
};
/** Beschriftungen der Ausgangs-Beziehungen (nur im Umwandlungs-Modus gesetzt). */
const parseLabels = (raw: unknown): Record<string, string> => {
  if (typeof raw !== "string" || !raw.trim()) return {};
  try {
    const p = JSON.parse(raw);
    return p && typeof p === "object" && !Array.isArray(p) ? p : {};
  } catch {
    return {};
  }
};

const UPLOAD_ICON = (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 12.5V3.5M6.5 7L10 3.5 13.5 7M4 15.5h12" />
  </svg>
);

const DOKUMENT_ICON = (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
    <path d="M5.5 2.5h6L15 6v11.5H5.5z" /><path d="M11.5 2.5V6H15" />
  </svg>
);

const ENTFERNEN_ICON = (
  <svg width="14" height="14" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round">
    <path d="M4.5 4.5l9 9M13.5 4.5l-9 9" />
  </svg>
);

const NewPrivateContactForm: React.FC<Props> = ({
  formFields,
  updateField,
  onSave,
  onSaveSuccess,
  onClose,
  convert,
  onTypWechsel,
  bearbeiten,
  speichernText,
}) => {
  const s = useStyles();
  const fs = useFormPrimitiveStyles();
  // Offene Vollanlagen der Schnellanlagen dieser Maske. In der Akte führt sie
  // die Akte selbst (ihr Schloss speichert ohne diese Maske).
  const eigeneVollanlagen = useOffeneVollanlagenRegister();
  const offeneVollanlagen = React.useContext(OffeneVollanlagenContext) ?? eigeneVollanlagen;
  const { hasPermission } = useAuth();

  const f = (k: string) => str(formFields[k]);
  const set = (k: string, v: string) => {
    // Ein Kontoinhaber, der auf den bisherigen Namen lautet (z. B. aus der
    // Akte geladen), zieht bei einer Namensänderung mit: geleert folgt er dem
    // Vorschlag (`kontoinhaberName`) und wird beim Speichern neu geschrieben.
    // Ein abweichend eingetragener Inhaber bleibt stehen.
    if ((k === "firstname" || k === "lastname") && v !== f(k)) {
      const inhaber = f("accountHolder").trim();
      if (inhaber && inhaber === kontoinhaberName) updateField("accountHolder", "");
    }
    updateField(k, v);
  };
  /**
   * PLZ oder Ort ändern — ein Ortsteil, der nicht mehr passt, fällt dabei weg
   * (utils/ortsteilAbgleich.ts, User 30.09.2026).
   */
  const setPlzOderOrt = (feld: "postalcode" | "city", v: string) => {
    const aenderung = feld === "postalcode" ? { plz: v } : { ort: v };
    if (ortsteilLeeren({ plz: f("postalcode"), ort: f("city"), ortsteil: f("cityArea") }, aenderung)) {
      set("cityArea", "");
    }
    set(feld, v);
  };
  // Bis 08/2026 stand hier ein Geschlecht-Auswahlfeld, das technisch „anrede"
  // hieß. Jetzt trägt das Feld die echte Anrede; das Geschlecht wird daraus
  // abgeleitet (utils/anredeGeschlecht) und im Payload mitgeschickt.
  const anredeOptionen = useAnredeOptionen();
  const syncJson = (k: string, val: unknown) => updateField(k, JSON.stringify(val));
  const empty = (k: string) => f(k).trim() === "";

  const oeffneAuthentifiziert = async (pfad: string): Promise<void> => {
    try {
      const res = await fetch(apiUrl(pfad));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const geoeffnet = window.open(objectUrl, "_blank", "noopener,noreferrer");
      if (geoeffnet) window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (e) {
      console.error("Dokument konnte nicht geöffnet werden:", e);
      await hinweisen({ titel: "Dokument nicht geöffnet", text: "Das Dokument konnte nicht geöffnet werden. Bitte später noch einmal versuchen.", ton: "danger" });
    }
  };

  const uploadFileToAuthorities = async (file: File): Promise<{ url: string; name?: string }> => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(apiUrl("upload/contact-authority"), { method: "POST", body: fd });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as { url: string; name?: string };
  };

  // ── Geburtsurkunde ──
  // Bis 09/2026 gab es hier einen eigenen Upload („Geburtsurkunde hochladen"),
  // der die neue Dokumentart doppelte. Stattdessen öffnet „Geburtsurkunde
  // scannen" den Scan (U7) — die Datei landet am Dokumenteintrag. Schon
  // hochgeladene Dateien (`birthCertificateUrl`) bleiben lesbar und löschbar.
  const [geburtsurkundeUrl, setGeburtsurkundeUrl] = React.useState<string>(() => f("birthCertificateUrl"));
  const [geburtsurkundeName, setGeburtsurkundeName] = React.useState<string>(() => f("birthCertificateName"));

  const handleRemoveGeburtsurkunde = () => {
    setGeburtsurkundeUrl("");
    setGeburtsurkundeName("");
    set("birthCertificateUrl", "");
    set("birthCertificateName", "");
  };

  // ── Ausweisdokumente (Liste inkl. Subtypen/Klassen und weiteren Dokumenten) ──
  const [documents, setDocuments] = React.useState<AusweisdokumentEintrag[]>(() => {
    const list: AusweisdokumentEintrag[] = [];
    const firstDoc: AusweisdokumentEintrag = {
      id: "doc-1",
      idCardType: f("idCardType") || "Personalausweis",
      idCardSubtype: f("idCardSubtype") || "",
      idCardClasses: f("idCardClasses") || "",
      idCardNumber: f("idCardNumber") || "",
      idCardIssuedAt: f("idCardIssuedAt") || "",
      idCardValidUntil: f("idCardValidUntil") || "",
      idCardAuthority: f("idCardAuthority") || "",
    };
    list.push(firstDoc);

    const rawAdditional = formFields.additionalDocuments;
    if (rawAdditional) {
      try {
        const parsed = JSON.parse(String(rawAdditional));
        if (Array.isArray(parsed)) {
          parsed.forEach((d: any, i: number) => {
            list.push({
              id: d.id || `doc-add-${i + 1}`,
              idCardType: d.idCardType || "Personalausweis",
              idCardSubtype: d.idCardSubtype || "",
              idCardClasses: d.idCardClasses || "",
              idCardNumber: d.idCardNumber || "",
              idCardIssuedAt: d.idCardIssuedAt || "",
              idCardValidUntil: d.idCardValidUntil || "",
              idCardAuthority: d.idCardAuthority || "",
            });
          });
        }
      } catch (e) {
        console.error("Fehler beim Parsen von additionalDocuments:", e);
      }
    }
    return list;
  });

  const handleDocumentsChange = (newDocs: AusweisdokumentEintrag[]) => {
    const docs = newDocs.length > 0 ? newDocs : [{
      id: "doc-1",
      idCardType: "Personalausweis",
      idCardSubtype: "",
      idCardClasses: "",
      idCardNumber: "",
      idCardIssuedAt: "",
      idCardValidUntil: "",
      idCardAuthority: "",
    }];
    setDocuments(docs);

    const primary = docs[0];
    set("idCardType", primary.idCardType || "Personalausweis");
    set("idCardSubtype", primary.idCardSubtype || "");
    set("idCardClasses", primary.idCardClasses || "");
    set("idCardNumber", primary.idCardNumber || "");
    set("idCardIssuedAt", primary.idCardIssuedAt || "");
    set("idCardValidUntil", primary.idCardValidUntil || "");
    set("idCardAuthority", primary.idCardAuthority || "");

    const additional = docs.slice(1);
    set("additionalDocuments", additional.length > 0 ? JSON.stringify(additional) : "");
  };

  // ── Weitere Kommunikation ──
  const [weitereKommOpen, setWeitereKommOpen] = React.useState(false);
  const [extraCommunications, setExtraCommunications] = React.useState<WeitereKommunikationEintrag[]>(() => {
    try {
      const parsed = JSON.parse(String(formFields.weitereKommunikation ?? "[]"));
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const addExtraComm = (entry: WeitereKommunikationEintrag) => {
    const next = [...extraCommunications, entry];
    setExtraCommunications(next);
    syncJson("weitereKommunikation", next);
  };

  const updateExtraComm = (id: string, patch: Partial<WeitereKommunikationEintrag>) => {
    const next = extraCommunications.map((c) => (c.id === id ? { ...c, ...patch } : c));
    setExtraCommunications(next);
    syncJson("weitereKommunikation", next);
  };

  const removeExtraComm = (id: string) => {
    const next = extraCommunications.filter((c) => c.id !== id);
    setExtraCommunications(next);
    syncJson("weitereKommunikation", next);
  };

  // ── Erreichbarkeit ──
  const [erreichbarkeitModalOpen, setErreichbarkeitModalOpen] = React.useState(false);
  const [allgemeineErreichbarkeit, setAllgemeineErreichbarkeit] = React.useState<string>(
    () => str(formFields.erreichbarkeit) || "",
  );
  const [erreichbarkeiten, setErreichbarkeiten] = React.useState<Record<string, string>>(() => {
    try {
      const parsed = JSON.parse(String(formFields.erreichbarkeitenDetail ?? "{}"));
      return typeof parsed === "object" && parsed !== null ? parsed : {};
    } catch {
      return {};
    }
  });

  const setErreichbarkeitField = (key: string, val: string) => {
    setErreichbarkeiten((prev) => {
      const next = { ...prev, [key]: val };
      syncJson("erreichbarkeitenDetail", next);
      return next;
    });
  };

  // Mobilnummer unter „Telefon" / Festnetz unter „Mobil": Hinweis + Knopf zum
  // Tauschen bzw. Verschieben, je Spalte getrennt (TelefonMobilHinweis.tsx).
  // Die Werte laufen über die normalen Setter — die ids der Kommunikations-
  // zeilen hängen am Feld (commDbIds), nicht am Wert.
  const erreichbarkeitPaar = (telefonKey: string, mobilKey: string) => (aktion: "tauschen" | "nachMobil" | "nachTelefon") => {
    setErreichbarkeiten((prev) => {
      if (!prev[telefonKey] && !prev[mobilKey]) return prev;
      const next = erreichbarkeitUmhaengen(prev, telefonKey, mobilKey, aktion);
      syncJson("erreichbarkeitenDetail", next);
      return next;
    });
  };
  const paarPrivat = useTelefonMobilPaar({
    telefon: f("phonePrivate"),
    mobil: f("mobilePrivate"),
    setTelefon: (v) => set("phonePrivate", v),
    setMobil: (v) => set("mobilePrivate", v),
    erreichbarkeitTauschen: erreichbarkeitPaar("phonePrivate", "mobilePrivate"),
  });
  const paarGeschaeftlich = useTelefonMobilPaar({
    telefon: f("phoneWork"),
    mobil: f("mobileWork"),
    setTelefon: (v) => set("phoneWork", v),
    setMobil: (v) => set("mobileWork", v),
    erreichbarkeitTauschen: erreichbarkeitPaar("phoneWork", "mobileWork"),
  });

  // Doppelte Nummer/E-Mail im selben Kontakt bzw. Nummer eines anderen
  // Kontakts (User 01.10.2026): nur Hinweise unter dem Feld, gespeichert wird
  // wie bisher. Dieselbe Liste bekommt das Popup „Weiterer Kommunikationsweg".
  const kontaktWege: KontaktWeg[] = [
    { key: "phonePrivate", typ: "phone", wert: f("phonePrivate"), feldname: "Telefon", zuordnung: "privat" },
    { key: "mobilePrivate", typ: "mobile", wert: f("mobilePrivate"), feldname: "Mobil", zuordnung: "privat" },
    { key: "emailPrivate", typ: "email", wert: f("emailPrivate"), feldname: "E-Mail", zuordnung: "privat" },
    { key: "phoneWork", typ: "phone", wert: f("phoneWork"), feldname: "Telefon", zuordnung: "geschaeftlich" },
    { key: "mobileWork", typ: "mobile", wert: f("mobileWork"), feldname: "Mobil", zuordnung: "geschaeftlich" },
    { key: "emailWork", typ: "email", wert: f("emailWork"), feldname: "E-Mail", zuordnung: "geschaeftlich" },
    ...weitereKommWege(extraCommunications, { mitZuordnung: true, faxHauptfeld: false }),
  ];
  const doppelt = doppelteWege(kontaktWege);
  const eigeneKontaktId = bearbeiten?.contactId ?? convert?.contactId ?? null;
  const andereKontakte = useNummernAndererKontakte(kontaktWege, {
    eigeneId: eigeneKontaktId,
    nurGeaenderte: eigeneKontaktId != null,
  });
  /** Hinweise „steht schon unter …"/„gehört schon zu …" eines Felds. */
  const wegHinweise = (key: string, typ: WegTyp) => [
    doppeltKommHinweis(typ, doppelt.get(key)),
    doppelt.has(key) ? null : andererKontaktKommHinweis(andereKontakte.get(key)),
  ];
  /** Alle Hinweise unter einem Hauptfeld, samt Platzhaltern für die Nachbarspalte. */
  const hauptHinweise = (feld: "telefon" | "mobil" | "email", spalte: "privat" | "geschaeftlich") => {
    const fuer = (sp: "privat" | "geschaeftlich") => {
      const paar = sp === "privat" ? paarPrivat : paarGeschaeftlich;
      const typ: WegTyp = feld === "telefon" ? "phone" : feld === "mobil" ? "mobile" : "email";
      const key = typ + (sp === "privat" ? "Private" : "Work");
      return [feld === "email" ? null : telefonMobilKommHinweis(feld, paar.hinweis, paar.ausfuehren), ...wegHinweise(key, typ)];
    };
    return <KommHinweise eigen={fuer(spalte)} nachbar={fuer(spalte === "privat" ? "geschaeftlich" : "privat")} />;
  };
  const extraHinweise = (c: WeitereKommunikationEintrag) => {
    const typ = c.type as WegTyp;
    return <KommHinweise eigen={wegHinweise(`extra-comm-${c.id}`, typ)} />;
  };

  const handleAllgemeinChange = (val: string) => {
    setAllgemeineErreichbarkeit(val);
    set("erreichbarkeit", val);
  };

  // ── Unternehmen fuer geschäftliche Kommunikationswege ──
  // Arbeitgeber + Firmen-Verknüpfungen; ein Weg kann mehreren gehören
  // (02.10.2026). Zur Auswahl stehen nur aktive Firmen. Gibt es genau EINE,
  // ist sie immer gesetzt — abgeleitet, nicht in den State geschrieben
  // (utils/geschaeftlichFirma.ts).
  const geschaeftlicheFirmenListe = React.useMemo(
    () => geschaeftlicheFirmen(formFields),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formFields.employerContactName, formFields.employerContactId, formFields.rel_personFirma],
  );
  const availableCompanies = React.useMemo<FirmaOption[]>(
    () => waehlbareFirmen(geschaeftlicheFirmenListe),
    [geschaeftlicheFirmenListe],
  );

  const [commCompanies, setCommCompanies] = React.useState<CommCompanies>(() => {
    try {
      const parsed = JSON.parse(String(formFields.commCompanies ?? "{}"));
      return typeof parsed === "object" && parsed !== null ? parsed : {};
    } catch {
      return {};
    }
  });

  /** Wirksame Unternehmen eines festen geschäftlichen Felds (gewählt + die einzige aktive). */
  const workFirmen = (fieldKey: string): FirmaOption[] =>
    slotFirmen(commCompanies, fieldKey, geschaeftlicheFirmenListe);
  /** Wirksame Unternehmen eines weiteren Eintrags. */
  const extraFirmen = (c: WeitereKommunikationEintrag): FirmaOption[] =>
    weitereFirmen(c, geschaeftlicheFirmenListe);
  /** „A und B" für Klartext-Beschriftungen (Rufnummern-Liste, Feldname). */
  const firmenText = (firmen: FirmaOption[]): string => firmenVoll(firmen.map((x) => x.name));

  /** Leere Liste = bewusst keine Zuordnung (ab zwei Firmen erlaubt). */
  const setCommCompany = (fieldKey: string, firmen: FirmaOption[]) => {
    setCommCompanies((prev) => {
      const next: CommCompanies = { ...prev };
      next[fieldKey] = firmen.filter((x) => x && x.name);
      syncJson("commCompanies", next);
      return next;
    });
  };

  const checkExistingComm = React.useCallback(
    (
      type: "phone" | "mobile" | "fax" | "email",
      zuordnung: "privat" | "geschaeftlich",
      firmen: FirmaOption[]
    ): boolean => {
      if (zuordnung === "privat") {
        if (type === "phone" || type === "mobile" || type === "email") return true;
        return extraCommunications.some(
          (c) => (!c.zuordnung || c.zuordnung === "privat") && c.type === type
        );
      }

      // geschaeftlich: Gibt es schon einen Weg dieses Typs für eines der
      // gewählten Unternehmen (bzw. ohne Unternehmen, wenn keins gewählt ist)?
      const teilt = (vorhanden: FirmaOption[]) =>
        firmen.length
          ? vorhanden.some((v) => firmen.some((w) => gleicheFirma(v, w)))
          : vorhanden.length === 0;
      const festesFeld = type === "phone" ? "phoneWork" : type === "mobile" ? "mobileWork" : type === "email" ? "emailWork" : null;
      if (festesFeld) {
        if (!firmen.length && f(festesFeld)) return true;
        if (teilt(workFirmen(festesFeld))) return true;
      }
      return extraCommunications.some(
        (c) => c.zuordnung === "geschaeftlich" && c.type === type && teilt(extraFirmen(c))
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [extraCommunications, commCompanies, geschaeftlicheFirmenListe, f]
  );

  /**
   * Label eines Kommunikationswegs: Titel, dahinter grau die Firma (bei
   * geschäftlichen Wegen anklickbar, siehe CommFirmaWahl) und die Bezeichnung.
   */
  const renderCommLabel = (
    baseTitle: string,
    firmaWahl?: React.ReactNode,
    bezeichnung?: string
  ) => {
    const bTrim = (bezeichnung ?? "").trim();
    const bez =
      bTrim && !["telefon", "mobil", "e-mail", "email", "fax"].includes(bTrim.toLowerCase()) ? bTrim : "";
    const grau: React.CSSProperties = {
      color: "#736871",
      fontSize: "0.75rem",
      fontWeight: 400,
      marginLeft: "6px",
    };

    return (
      <span>
        <span>{baseTitle}</span>
        {firmaWahl ? <span style={{ marginLeft: "6px" }}>{firmaWahl}</span> : null}
        {bez ? <span style={grau}>{firmaWahl ? `- ${bez}` : bez}</span> : null}
      </span>
    );
  };

  /**
   * Unternehmen eines geschäftlichen Wegs als grauer, anklickbarer Text im
   * Label — `null`, wenn nichts anzuzeigen ist (keins, höchstens eins wählbar).
   */
  const firmaWahl = (
    firmen: FirmaOption[],
    onChange: (firmen: FirmaOption[]) => void
  ): React.ReactNode =>
    commFirmaText(firmen, availableCompanies) ? (
      <CommFirmaWahl firmen={availableCompanies} gewaehlt={firmen} onChange={onChange} />
    ) : null;

  /** Unternehmen eines weiteren geschäftlichen Eintrags setzen; `[]` = bewusst keins. */
  const setExtraCommFirma = (id: string, firmen: FirmaOption[]) =>
    updateExtraComm(id, { firmen, companyId: undefined, companyName: undefined, keineFirma: undefined });

  const getPlainCommLabel = (
    baseTitle: string,
    companyName?: string,
    bezeichnung?: string
  ) => {
    const parts: string[] = [];
    if (companyName && companyName.trim()) parts.push(companyName.trim());
    if (bezeichnung && bezeichnung.trim()) {
      const bTrim = bezeichnung.trim();
      if (!["telefon", "mobil", "e-mail", "email", "fax"].includes(bTrim.toLowerCase())) {
        parts.push(bTrim);
      }
    }
    const suffix = parts.join(" - ");
    return suffix ? `${baseTitle} (${suffix})` : baseTitle;
  };

  // ── Einladungsdateien (Karte Zuwendungen) — nach dem Anlegen abgelegt ──
  const pendingEinladungFilesRef = React.useRef<File[]>([]);

  // ── Hochzeitsdokumente (Einladung, Mitteilung) ──
  const [hochzeitDateien, setHochzeitDateien] = React.useState<HochzeitsDateiItem[]>([]);
  const pendingHochzeitFilesRef = React.useRef<Map<string, File>>(new Map());

  const handleAddHochzeitDateien = (files: File[]) => {
    const newItems: HochzeitsDateiItem[] = files.map((f) => {
      const id = `hz-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      pendingHochzeitFilesRef.current.set(id, f);
      return { id, name: f.name, file: f };
    });
    setHochzeitDateien((prev) => [...prev, ...newItems]);
  };

  const handleRemoveHochzeitDatei = (id: string) => {
    pendingHochzeitFilesRef.current.delete(id);
    setHochzeitDateien((prev) => prev.filter((d) => d.id !== id));
  };

  // Karte „Vollmachten": Auswahl vor dem Speichern, erzeugt wird danach.
  const [vollmachten, setVollmachten] = React.useState<VollmachtAuswahl[]>(standardVollmachtAuswahl);

  // ── Verfügungen & Vertretungen ──
  const [verfuegungItems, setVerfuegungItems] = React.useState<VerfuegungStateItem[]>(() => {
    const raw = parseArr(formFields.verfuegungen);
    return raw.map((v: any) => ({
      art: v.art,
      aktiv: true,
      bevollmaechtigterId: v.bevollmaechtigterContactId ?? v.bevollmaechtigterId,
      bevollmaechtigterName: v.bevollmaechtigterName,
      bevollmaechtigterRef: v.bevollmaechtigterRef ?? null,
      fileUrl: v.fileUrl,
      fileName: v.fileName,
    }));
  });

  const handleVerfuegungenChange = (list: VerfuegungStateItem[]) => {
    setVerfuegungItems(list);
    const toStore = list
      .filter((v) => v.aktiv)
      .map((v) => ({
        art: v.art,
        bevollmaechtigterContactId: v.bevollmaechtigterId ?? null,
        bevollmaechtigterName: v.bevollmaechtigterName ?? null,
        bevollmaechtigterRef: v.bevollmaechtigterRef ?? null,
        fileUrl: v.fileUrl ?? null,
        fileName: v.fileName ?? null,
      }));
    set("verfuegungen", JSON.stringify(toStore));
  };

  const [vertretungItems, setVertretungItems] = React.useState<VertretungStateItem[]>(() => {
    const raw = parseArr(formFields.rel_vertretung);
    return raw.map((it: any) => ({
      id: it.id,
      rolle: it.rolle || (Array.isArray(it.rollen) ? it.rollen[0] : ""),
      rollen: Array.isArray(it.rollen) ? it.rollen : (it.rolle ? [it.rolle] : ["Vorsorgevollmacht"]),
      zielContactId: it.ref?.contactId ?? it.zielContactId,
      zielName: it.ref?.displayName ?? it.zielName ?? "Unbekannt",
      zielRef: it.ref || null,
      fileUrl: it.fileUrl,
      fileName: it.fileName,
    }));
  });

  const handleVertretungChange = (list: VertretungStateItem[]) => {
    setVertretungItems(list);
    const toStore = list.map((v) => ({
      id: v.id,
      rolle: v.rolle || (v.rollen && v.rollen[0]) || "",
      rollen: v.rollen || [],
      ref: v.zielRef || (v.zielContactId ? { contactId: v.zielContactId, displayName: v.zielName } : null),
      fileUrl: v.fileUrl || null,
      fileName: v.fileName || null,
    }));
    set("rel_vertretung", JSON.stringify(toStore));
  };

  // ── „Datei scannen": Visitenkarte, Ausweis, Abrechnung oder vCard ─────────
  // Fuellt nur LEERE Felder; was schon dasteht, bleibt unangetastet.
  // Welcher Scan offen ist: der allgemeine oben („Datei scannen"), die
  // Gehaltsabrechnung oder die Ausweisdokumente (User 26.09.2026).
  const [scanModus, setScanModus] = React.useState<"alle" | "gehalt" | "dokumente" | "geburtsurkunde" | null>(null);
  // Ein Scan-Knopf an jedem Dokumenteintrag (28.09.2026) ist seit 02.10.2026
  // wieder weg: er öffnete denselben Dialog wie der Knopf über der Liste. Das
  // Ziel wählt `dokumentScanVerteilen` nach dem Lesen selbst.
  // Dateien an den Dokumenteinträgen — abgelegt wird nach dem Speichern.
  const dokDateien = useDokumentDateien();


  /**
   * Steht in dem Feld nur die VORGABE der Maske, nicht die Eingabe eines
   * Menschen?
   *
   * Die Staatsangehoerigkeit steht beim Anlegen von sich aus auf „Deutsch"
   * (`NationalitySelect`, `autoDefault`). Fuer die Regel „nur leere Felder
   * fuellen" ist dieses Feld damit NIE leer — ein Ausweisdokument mit anderer
   * Staatsangehoerigkeit kaeme also nie durch, und in der Maske bliebe still
   * „Deutsch" stehen. Eine Vorgabe ist aber keine Angabe.
   */
  const istVorgabewert = (key: string, wert: string): boolean =>
    key === "citizenship" && wert.trim() === DEFAULT_NATIONALITY;

  /**
   * Uebernimmt die Werte eines eingelesenen Dokuments.
   *
   * Nur leere Felder werden gefuellt: der Dialog bietet belegte Felder gar
   * nicht erst an, und was hier trotzdem ankommt, soll Getipptes nicht
   * ueberschreiben. Katalog-gebundene Felder (Anrede, Titel) sind im Mapper
   * bereits gegen den jeweiligen Katalog geprueft — ein Wert, den die Auswahl
   * nicht kennt, kommt hier gar nicht an, sondern wird im Dialog als nicht
   * uebernommen angezeigt.
   */
  const applyDateiScan = (ergebnis: DateiScanUebernahme) => {
    const leerOderVorgabe = (key: string) => empty(key) || istVorgabewert(key, f(key));
    const neueId = () => "doc-" + Date.now() + "-" + Math.random().toString(36).substring(2, 6);

    // Dokumentfelder gehen an den passenden Dokumenteintrag, nicht mehr
    // pauschal an `documents[0]` (siehe `dokumentScanVerteilen`).
    const verteilt = dokumentScanVerteilen({
      werte: ergebnis.werte,
      scanArt: ergebnis.scanArt,
      docs: documents,
      zielId: null,
      neueId,
    });

    // Visitenkarte: alle Kanäle sind geschäftlich und gehören der Firma der
    // Karte (User 28.09.2026) — Zuordnung je Kanal, Fax als weiterer Eintrag.
    const firmenbezug = visitenkarteFirmenbezug({
      werte: verteilt.personWerte,
      firma: ergebnis.firma,
      wirdGefuellt: leerOderVorgabe,
      // Nur ausdrücklich gewählte Firmen — die Karte kennt ihre Firma genauer
      // als die Automatik, und ein bewusstes „(Keine Firma)“ ist hier offen.
      zugeordnet: Object.fromEntries(
        Object.entries(commCompanies).map(([k, v]) => [k, gespeicherteFirmen(v)?.[0]]),
      ),
      weitere: extraCommunications,
      neueId: () => "komm-" + Date.now() + "-" + Math.random().toString(36).substring(2, 6),
    });

    for (const [key, wert] of Object.entries(firmenbezug.werte)) {
      const v = String(wert ?? "").trim();
      if (!v) continue;
      if (!leerOderVorgabe(key)) continue;
      set(key, v);
    }
    for (const [key, firma] of Object.entries(firmenbezug.zuordnungen)) setCommCompany(key, [firma]);
    if (firmenbezug.weitereNeu.length) {
      const next = [...extraCommunications, ...firmenbezug.weitereNeu];
      setExtraCommunications(next);
      syncJson("weitereKommunikation", next);
    }

    if (verteilt.docs) handleDocumentsChange(verteilt.docs);
    if (ergebnis.ablegen && verteilt.zielId) dokDateien.setzen(verteilt.zielId, ergebnis.ablegen);

    // Die Firma der Karte kommt unter „Verbindung zu Unternehmen" (P2–P6) —
    // ein leer angefangener Eintrag wird gefüllt, sonst angehängt. Angelegt
    // wird sie erst beim Speichern (P9). Der Arbeitgeber wird daraus
    // abgeleitet (P7): eine bestehende Firma über `arbeitgeberVorschlag`,
    // eine neue als neu eingetippter Arbeitgeber, solange noch keiner gewählt
    // ist (utils/visitenkarteVerbindung.ts).
    // Heiratsurkunde: der andere Ehegatte als Vorbelegung im Haushalt (U3) —
    // angelegt wird er erst beim Speichern, wie jede Haushaltszeile.
    if (ergebnis.ehepartner) {
      const vorher = parseArr(formFields.rel_household) as HouseholdItem[];
      const next = ehepartnerInHaushalt(vorher, ergebnis.ehepartner);
      if (next !== vorher) set("rel_household", JSON.stringify(next));
    }

    if (ergebnis.firmaVerbindung) {
      const vorher = parseArr(formFields.rel_personFirma) as PersonFirmaItem[];
      const { items, alsNeuerArbeitgeber } = firmaInVerbindungen({
        items: vorher,
        firma: ergebnis.firmaVerbindung.ref,
        position: ergebnis.firmaVerbindung.position,
        arbeitgeberFrei: !f("employerContactId").trim() && !arbeitgeberDraft.isNew,
      });
      if (items !== vorher) set("rel_personFirma", JSON.stringify(items));
      if (alsNeuerArbeitgeber) {
        const ref: ContactRef = { ...ergebnis.firmaVerbindung.ref, type: "Firma", manual: true };
        updateArbeitgeberDraft({ isNew: true, ref });
        set("employerContactName", ref.lastName ?? "");
      }
    }
  };


  // Steuer-, Sozial- und Ausweisangaben standen bis 09/2026 nur den
  // Verantwortlichen des Kontakts (FO/BO) sowie gfgl/admin offen; die Maske
  // blendete sie sonst aus, weil der Server sie ohnehin verworfen hätte. Der
  // PO hat diese Sonderstellung am 21.09.2026 aufgehoben — sie werden wie
  // jede andere Kontaktangabe erfasst, und die Felder stehen deshalb jedem
  // offen, der einen Kontakt anlegen darf.

  // --- Beruf: Rentner und weitere Tätigkeit (data/berufRentner.ts) ---------
  const berufSicht = berufSichtbarkeit(f("occupationStatus"), f("weitereTaetigkeit"));

  /**
   * Der Arbeitgeber steht sofort unter „Verbindung zu Unternehmen"
   * (utils/arbeitgeberVerbindung.ts, User 30.09.2026): gewählt → die Firma
   * kommt dazu, falls sie fehlt; gelöst/gewechselt → ein unveränderter
   * automatischer Eintrag fällt wieder weg.
   */
  const arbeitgeberVerbindungSetzen = (ref: ContactRef | null) => {
    const vorher = parseArr(formFields.rel_personFirma) as PersonFirmaItem[];
    const next = arbeitgeberSpiegeln(vorher, ref);
    if (next !== vorher) set("rel_personFirma", JSON.stringify(next));
    // Bleibt die alte Firma (verändert) in der Liste, darf die Vorbelegung sie
    // nicht gleich wieder als Arbeitgeber einsetzen.
    const alt = Number(arbeitgeberId);
    if (alt > 0 && Number(ref?.contactId) !== alt) vorbelegtRef.current = alt;
  };

  /** Arbeitgeber-Verknüpfung (und eine halb begonnene Neuanlage) verwerfen. */
  const arbeitgeberLeeren = () => {
    updateArbeitgeberDraft({ isNew: false, ref: undefined });
    set("employerContactId", "");
    set("employerContactName", "");
    arbeitgeberVerbindungSetzen(null);
  };

  /** Weitere Tätigkeit des Rentners setzen — Verstecktes wird dabei geleert. */
  const weitereTaetigkeitSetzen = (weitere: string) => {
    const status = f("occupationStatus");
    for (const feld of berufFelderZuLeeren(
      { status, weitere: f("weitereTaetigkeit") },
      { status, weitere },
    )) {
      if (feld === "employer") arbeitgeberLeeren();
      else set(feld, "");
    }
    set("weitereTaetigkeit", weitere);
  };

  // --- Beruf-Autofill aus den Firmen-Verbindungen (nur leere Felder) ----------
  // PersonFirmaItem[] → BerufConnection[] (Rollen + Firmenname). Abgeleitet
  // werden daraus nur noch Berufsstatus und Tätigkeit — Arbeitgeber und Branche
  // stehen in der Firmenakte.
  const berufConns = React.useMemo<BerufConnection[]>(() => {
    const items = parseArr(formFields.rel_personFirma) as PersonFirmaItem[];
    return items
      .filter((it) => it.ref && it.verant?.roles?.length)
      .map((it) => {
        const extern = !!it.verant?.isExtern;
        const firma = extern ? it.verant?.externalCompany : it.ref;
        return {
          isExtern: extern,
          roles: it.verant!.roles,
          companyName: firma?.displayName || firma?.lastName || "",
          companyBranche: firma?.branche || "",
        } as BerufConnection;
      });
  }, [formFields.rel_personFirma]);

  /** Verknüpfter Arbeitgeber (Firmen-Kontakt-Id) — "" solange keiner gewählt ist. */
  const arbeitgeberId = f("employerContactId").trim();

  // Status / Arbeitgeber / Tätigkeit (+ ggf. Freitext-Branche) füllen — nur leere.
  React.useEffect(() => {
    // Bearbeiten-Modus: Beruf steht im Bestand; eine Ableitung beim Öffnen
    // landete im Ladestand der Akte und würde nie gespeichert.
    if (bearbeiten) return;
    const d = deriveBerufFromConnections(berufConns);
    if (d.jobStatus && empty("occupationStatus")) {
      let mapped = d.jobStatus;
      if (/angestellt|arbeiter/i.test(d.jobStatus)) mapped = "Angestellt";
      else if (/beamte/i.test(d.jobStatus)) mapped = "Beamter";
      else if (/selbst|freiberuf/i.test(d.jobStatus)) mapped = "Selbständig/Freiberufler";
      set("occupationStatus", mapped);
    }
    if (d.jobOccupation && empty("position")) set("position", d.jobOccupation);
    // Arbeitgeber und Branche werden NICHT mehr vorbelegt: Beide stehen in der
    // Firmenakte und werden von dort angezeigt. Sie zusätzlich am Privatkontakt
    // zu führen hieße, jeden Umzug und jeden Branchenwechsel zweimal zu pflegen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [berufConns, arbeitgeberId]);

  /**
   * Vorbelegung des Arbeitgebers aus den Firmen-Verknüpfungen.
   *
   * Maßgeblich ist der Haken „Mensch im Unternehmen" der Verantwortlichkeit —
   * nicht einfach die erste Firma: Eine reine Beteiligung oder ein externes
   * Mandat (Steuerberater) ist kein Anstellungsverhältnis, und genau diese
   * Unterscheidung trifft der Haken bereits. Genau EINE solche Firma wird
   * vorbelegt; bei mehreren entscheidet der Anwender selbst.
   */
  const arbeitgeberVorschlag = React.useMemo(() => {
    const items = parseArr(formFields.rel_personFirma) as PersonFirmaItem[];
    const treffer = items.filter(
      (it) => it.ref?.contactId && !it.verant?.isExtern && it.verant?.istMenschImUnternehmen,
    );
    if (treffer.length !== 1) return null;
    const ref = treffer[0].ref!;
    return { id: ref.contactId as number, name: ref.displayName || ref.lastName || "Firma" };
  }, [formFields.rel_personFirma]);

  /**
   * Welche Firma zuletzt vorbelegt wurde — damit die Vorbelegung sich nicht
   * gegen den Anwender durchsetzt.
   *
   * „Verknüpfung lösen" leert `employerContactId`; ohne dieses Gedächtnis liefe
   * der Effekt sofort erneut (die Id steht in seinen Abhängigkeiten) und setzte
   * dieselbe Firma wieder ein. Der Freitext-Arbeitgeber wäre damit unerreichbar,
   * sobald genau EINE interne Firmen-Verbindung erfasst ist — also im Regelfall,
   * für den die Vorbelegung überhaupt gebaut wurde.
   */
  const vorbelegtRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    // Bearbeiten-Modus: Der Arbeitgeber kommt aus der gespeicherten Beziehung.
    // Eine Vorbelegung landete im Ladestand und würde nie gespeichert.
    if (bearbeiten) return;
    if (!arbeitgeberVorschlag) {
      // Vorschlag weggefallen (Verbindung entfernt oder geändert) — eine neue
      // darf wieder vorbelegen.
      vorbelegtRef.current = null;
      return;
    }
    if (arbeitgeberId || vorbelegtRef.current === arbeitgeberVorschlag.id) return;
    vorbelegtRef.current = arbeitgeberVorschlag.id;
    set("employerContactId", String(arbeitgeberVorschlag.id));
    set("employerContactName", arbeitgeberVorschlag.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arbeitgeberVorschlag, arbeitgeberId]);

  // Die Branche der verbundenen Firma wurde hier früher nachgeladen und in ein
  // Freitextfeld am Privatkontakt geschrieben. Das Feld gibt es nicht mehr: Die
  // Branche steht in der Firmenakte und wird unter dem Arbeitgeber von dort
  // ANGEZEIGT (ArbeitgeberAuswahl) — kopiert wird sie nicht.
  // Pflichtfelder Privat: Nachname und Vorname; eine Rufnummer (Mobil (privat)
  // ODER Telefon (privat)) kommt nur bei Kunde/Sonstige dazu. Die Regel lebt in
  // privateRequiredChecks (dort getestet).
  // Im Umwandlungs-Modus gilt **kein einziges** Pflichtfeld: der Kontakt
  // existiert bereits und wechselt nur seinen Typ — ein unvollständiger
  // Altbestand (eine Firma hat z. B. nie einen Vornamen) darf dabei nicht am
  // Speichern gehindert werden. Leeres Check-Array ⇒ requiredOk ist true.
  // Ansprechpartner bei einem Produktpartner: die Maske zeigt dann statt der
  // Kunden-Abschnitte (Herkunft, Beruf, Bank, Familie, Haushalt) die Angaben zur
  // Gesellschaft. Die Rufnummer ist dabei nicht Pflicht — von vielen
  // Gesellschaftskontakten kennt man zunächst nur die E-Mail-Adresse.
  // Nur der Primärstatus bestimmt das Partnerlayout. Ein zusätzlicher
  // Partnerstatus an einem Kunden ändert dessen Formular nicht.
  const gewaehlteRollen = rollenAusFeld(f("kontaktStatusRollen"));
  const isApProduktpartner =
    f("kontaktStatus") === STATUS_SONSTIGE &&
    !f("kontaktStatusAuch") && isPartnerStatus(gewaehlteRollen[0]);

  // Kontaktstatus — der FACHLICHE Wert, roh aus `kontaktStatus`. Er steuert das
  // Untertyp-Feld, die Herkunfts-Optionen und (in der Firmenmaske) den
  // NKG-Block, und er geht als `statusName` an die API. „Interessent" ist der
  // Default der Anlage, wie in `buildPrivateContactCreatePayload`.
  //
  // Bewusst NICHT aus dem Segment abgeleitet: ein persönlicher Interessent
  // bleibt Interessent. Was das obere Segment anzeigt, ist eine Projektion über
  // zwei Feldern — siehe data/kontaktstatusSegment.ts.
  const contactStatus = f("kontaktStatus") || "Interessent";
  const isInteressent = contactStatus === "Interessent";


  const requiredChecks = convert
    ? []
    : privateRequiredChecks({
        lastname: f("lastname"),
        firstname: f("firstname"),
        mobilePrivate: f("mobilePrivate"),
        phonePrivate: f("phonePrivate"),
        isApProduktpartner,
        isInteressent,
      });
  const requiredOk = requiredChecks.every(Boolean);
  // NKG-Vorgang (Neukundengewinnung) — seit 27.09.2026 auch bei Privat. Default „Nein".
  const [nkgWanted, setNkgWanted] = React.useState(false);
  const requiredFilled = requiredChecks.filter(Boolean).length;

  // ── lokale Listen-States (Anschriften, Banken, Verknüpfungen) ──────────
  const [extraAddresses, setExtraAddresses] = React.useState<any[]>(() =>
    parseArr(formFields.additionalAddresses),
  );
  const [extraBanks, setExtraBanks] = React.useState<any[]>(() =>
    parseArr(formFields.additionalBanks),
  );
  const [vereine, setVereine] = React.useState<VereinEintrag[]>(() => {
    try {
      const raw = String(formFields.vereine ?? "[]");
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    } catch {
      // ignore
    }
    if (typeof formFields.memberships === "string" && formFields.memberships.trim()) {
      return formFields.memberships.split(";").map((part, idx) => ({
        id: `v-${idx}`,
        vereinName: part.trim(),
        hatMitgliedschaft: true,
        funktion: "Mitglied",
      }));
    }
    return [];
  });

  const onVereineChange = (next: VereinEintrag[]) => {
    setVereine(next);
    syncJson("vereine", next);
    const summary = next
      .filter((v) => (v.vereinName || v.ref?.lastName || "").trim())
      .map((v) => {
        const name = (v.vereinName || v.ref?.lastName || "").trim();
        return `${name}${v.funktion ? ` (${v.funktion})` : ""}`;
      })
      .join("; ");
    set("memberships", summary);
  };
  const [arbeitgeberDraft, setArbeitgeberDraft] = React.useState<{
    isNew: boolean;
    ref?: ContactRef;
  }>(() => {
    try {
      const raw = String(formFields.arbeitgeberDraft ?? "");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (typeof parsed === "object" && parsed !== null) return parsed;
      }
    } catch {}
    return { isNew: false };
  });
  const updateArbeitgeberDraft = (next: { isNew: boolean; ref?: ContactRef }) => {
    setArbeitgeberDraft(next);
    syncJson("arbeitgeberDraft", next);
  };
  const [befuellenArbeitgeberOffen, setBefuellenArbeitgeberOffen] = React.useState(false);

  // Arbeitgeber-Auswahl und -Schnellanlage stehen im Beruf-Block an zwei
  // Stellen: neben dem Status (Angestellt/Beamter/Selbstständig) und beim
  // Rentner mit weiterer Tätigkeit neben „Weiterer Status".
  const arbeitgeberWaehlen = (
    id: number | null,
    firmenname?: string,
    firma?: { legalForm?: string; legalFormId?: number },
  ) => {
    set("employerContactId", id == null ? "" : String(id));
    set("employerContactName", id == null ? "" : firmenname ?? "Firma");
    updateArbeitgeberDraft({ isNew: false });
    arbeitgeberVerbindungSetzen(
      id == null
        ? null
        : {
            contactId: id,
            type: "Firma",
            lastName: firmenname ?? "Firma",
            displayName: firmenname ?? "Firma",
            ...(firma?.legalForm ? { legalForm: firma.legalForm } : {}),
            ...(firma?.legalFormId ? { legalFormId: firma.legalFormId } : {}),
          },
    );
  };
  const arbeitgeberAuswahl = (
    <ArbeitgeberAuswahl
      firmaId={arbeitgeberId}
      name={f("employerContactName")}
      onWaehlen={arbeitgeberWaehlen}
      isNew={arbeitgeberDraft.isNew}
      onStartNeuanlage={(initialName) => {
        const ref: ContactRef = {
          type: "Firma",
          manual: true,
          lastName: initialName,
        };
        updateArbeitgeberDraft({ isNew: true, ref });
        set("employerContactId", "");
        set("employerContactName", initialName);
        arbeitgeberVerbindungSetzen(ref);
      }}
      onCancelNeuanlage={() => {
        updateArbeitgeberDraft({ isNew: false, ref: undefined });
        set("employerContactId", "");
        set("employerContactName", "");
        arbeitgeberVerbindungSetzen(null);
      }}
    />
  );
  const arbeitgeberSchnellanlage = (
    arbeitgeberDraft.isNew && (
      <ArbeitgeberSchnellanlage
        refData={arbeitgeberDraft.ref}
        onChange={(patch) => {
          const updatedRef = {
            type: "Firma" as const,
            manual: true,
            ...(arbeitgeberDraft.ref ?? {}),
            ...patch,
          };
          if (updatedRef.lastName !== undefined) {
            set("employerContactName", updatedRef.lastName);
          }
          updateArbeitgeberDraft({
            isNew: true,
            ref: updatedRef,
          });
          arbeitgeberVerbindungSetzen(updatedRef);
        }}
        onOpenBefuellen={() => setBefuellenArbeitgeberOffen(true)}
        // Voll angelegt (User 02.10.2026): ab jetzt ein bestehender
        // Arbeitgeber — wie aus der Suche gewählt, kein zweites Anlegen.
        onAngelegt={(id, ref) =>
          arbeitgeberWaehlen(id, ref.displayName || ref.lastName, {
            legalForm: ref.legalForm,
            legalFormId: ref.legalFormId,
          })
        }
      />
    )
  );

  // Zentrale für die automatische Durchwahl der geschäftlichen Festnetz-/
  // Faxnummern: die Nummern der Firmen der Person (Arbeitgeber, Verbindungen —
  // gespeicherte werden geladen, neu eingetippte kommen aus dem Entwurf) und
  // der gemeinsame Anfang ihrer eigenen geschäftlichen Nummern.
  const neueFirmenNummern = React.useMemo(() => {
    const nummern: Array<string | undefined> = [];
    if (arbeitgeberDraft.isNew) nummern.push(arbeitgeberDraft.ref?.phone);
    for (const it of parseArr(formFields.rel_personFirma) as PersonFirmaItem[]) {
      const firma = it.verant?.isExtern ? it.verant?.externalCompany : it.ref;
      if (firma && !(firma as any).contactId && !(firma as any).id) nummern.push(firma.phone);
    }
    return nummern;
  }, [arbeitgeberDraft, formFields.rel_personFirma]);
  const geschaeftlicheZentrale = usePersonZentrale({
    firmaIds: availableCompanies.map((c) => c.id),
    firmenNummern: neueFirmenNummern,
    eigeneNummern: [
      f("phoneWork"),
      ...extraCommunications
        .filter((c) => c.zuordnung === "geschaeftlich" && (c.type === "phone" || c.type === "fax"))
        .map((c) => c.value),
    ],
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  /** Beziehungen, die beim Speichern ersatzlos entfallen — erst nach Nachfrage. */
  const [removalPrompt, setRemovalPrompt] = React.useState<string[] | null>(null);

  /**
   * Vorbelegung des Kontoinhabers: der Name der Person in Kontoschreibweise.
   * Er wird beim Speichern WIRKLICH in die Bankverbindung geschrieben
   * (buildPrivateContactCreatePayload), nicht nur angezeigt. Solange das Feld
   * im Formular leer ist, hat es niemand angefasst — dann folgt der Vorschlag
   * jeder Namensänderung; wer selbst etwas einträgt, behält seinen Eintrag.
   */
  const kontoinhaberName = kontoinhaberVorschlag(
    { firstName: f("firstname"), lastname: f("lastname") },
    false,
  );

  const pendingPhotoRef = React.useRef<File | null>(null);
  const [photoName, setPhotoName] = React.useState<string>(f("photo_url"));

  // Format-Gate (block + warn): jedes BEFÜLLTE Feld muss gültig sein (leer = ok),
  // inkl. der Wiederhol-Blöcke. Speichern ist nur bei gültigem Format möglich.
  // Weitere Kommunikationswege (Diensthandy, zweite Mail …) — JSON im Formular.
  const weitereWege = wegeLesen(formFields.weitereKommunikation);
  // Wahl der Newsletter-Adressen: alle E-Mail-Adressen, die gerade in der Maske stehen.
  const newsletterEmailOptionen = [
    { wert: f("emailPrivate"), art: "privat" },
    { wert: f("emailWork"), art: "geschäftlich" },
    ...weitereWege.filter((w) => w.type === "email").map((w) => ({ wert: w.value, art: "weitere" })),
  ]
    .map((e) => ({ ...e, wert: (e.wert ?? "").trim() }))
    .filter((e, i, alle) => e.wert && alle.findIndex((x) => x.wert === e.wert) === i)
    .map((e) => ({ value: e.wert, label: `${e.wert} (${e.art})` }));

  const formatValid = [
    ...weitereWege.map(wegFormatOk),
    validatePhone(f("phonePrivate")).valid,
    validatePhone(f("phoneWork")).valid,
    validatePhone(f("mobilePrivate")).valid,
    validatePhone(f("mobileWork")).valid,
    validateEmail(f("emailPrivate")).valid,
    validateEmail(f("emailWork")).valid,
    validateIban(f("iban")).valid,
    validateBic(f("bic")).valid,
    // Gesperrt wird nur, wo die Regel bewiesen ist — bei ausdrücklich
    // deutschen Adressen. Eine belgische PLZ bekommt einen Hinweis, aber
    // der Speichern-Knopf bleibt offen.
    !plzSperrtSpeichern(f("postalcode"), f("country")),
    ...extraAddresses.map((a) => !plzSperrtSpeichern(str(a.postalCode), str(a.country))),
    ...extraBanks.flatMap((b) => [validateIban(str(b.iban)).valid, validateBic(str(b.bic)).valid]),
  ].every(Boolean);

  // ── Herkunft (Eigenakquise-Default + Either/Or) ───────────────────────
  const { contactOrigins, contactOriginOther, toggleContactOrigin, setContactOriginOtherValue } =
    useContactOrigins({
      formFields,
      updateField,
      // Die Auswahl steht im Formular (herkunftEmpfehlungsgeber/-Tippgeber);
      // der Hook leert sie dort selbst.
      onClearReferrer: () => {},
      onClearTippgeber: () => {},
      onClearKundenverbindung: () => {},
    // Herkunft gehört zur Anlage; im Umwandlungs- und Bearbeiten-Modus würde
    // der Default-Effekt das Formular ungefragt als geändert markieren.
    enabled: !convert && !bearbeiten,
    });

  // ── Live-Dubletten-Prüfung (Vorname+Nachname) ─────────────────────────
  const dup = useDuplicateCheck({
    isCompany: false,
    firstName: f("firstname"),
    lastName: f("lastname"),
    birthDate: f("dateofbirth"),
    email: f("emailPrivate"),
    // Beim Umwandeln aus: die Maske ist mit dem Namen des bestehenden Kontakts
    // vorausgefüllt, die Suche fände also ihn selbst und meldete ihn als
    // Doppelgänger. Dasselbe gilt im Bearbeiten-Modus der Akte.
    enabled: !convert && !bearbeiten,
  });

  // ── Foto ──────────────────────────────────────────────────────────────
  const onPhotoPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    pendingPhotoRef.current = file;
    setPhotoName(file.name);
    set("photo_url", file.name);
  };
  const uploadPendingPhoto = async (contactId: number): Promise<void> => {
    const file = pendingPhotoRef.current;
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await fetch(apiUrl("upload/contact-photo"), { method: "POST", body: fd });
      if (!up.ok) return;
      const { url } = await up.json();
      await fetch(apiUrl(`contact/${contactId}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ photo_url: url }),
      });
      pendingPhotoRef.current = null;
    } catch {
      /* Foto-Fehler darf Anlage nicht abbrechen */
    }
  };

  // Die gesammelten Beziehungen — EINE Quelle für das Speichern und für die
  // Vorschau „was entfällt", damit der Dialog nichts anderes ankündigt, als
  // anschließend passiert.
  const collectedRelations = () => ({
    household: parseArr(formFields.rel_household),
    weitere: parseArr(formFields.rel_weitere),
    assistenz: (parseArr(formFields.rel_assistenz) as AssistantItem[]).filter(
      (a) => a.contactId || (a.lastName && a.lastName.trim()),
    ),
    personFirma: parseArr(formFields.rel_personFirma),
    // Vertretungen: in der Akte sofort gespeichert, hier gesammelt — der
    // Kontakt existiert beim Ausfüllen noch nicht.
    vertretung: parseArr(formFields.rel_vertretung),
    // Die Rückfrage „für wen?" liefert dieselbe Art Eintrag wie die Listen
    // darunter und wird genauso gespeichert. `imHaushalt` trägt die Rolle
    // selbst bei sich, damit `persistRelations` den Katalog nicht kennen muss —
    // und damit Statusblock und Haushaltsliste DENSELBEN Datenpfad nehmen.
    statusBezug: bezuegeAusFeld(formFields.kontaktStatusBezuege)
      .filter((b) => b.ref)
      .map((b) => ({
        id: `bezug-${b.rolle}`,
        ref: b.ref,
        rolle: b.rolle,
        imHaushalt: haushaltsAngabeVon(STATUS_EINTRAEGE_PERSON, b.rolle)?.imHaushalt,
        verwandtschaft: b.verwandtschaft,
        freitext: b.freitext,
        freitextRueck: b.freitextRueck,
      })),
  });

  // ── Gesammelte Verknüpfungen persistieren (ERST nach Anlage der Person) ───
  // Inline gesammelt (rel_*-Keys); frei eingetippte Sub-Kontakte werden hier
  // angelegt, dann die Beziehungen + KV-Zuweisung. Nichts davon vorher.
  const persistRelations = async (contactId: number, sync = false): Promise<void> => {
    // FO (auch mehrere), BO und persönlicher Inhouse-Kontakt — dieselbe
    // Verantwortlichkeit wie der Hauptkontakt (User 28.09.2026).
    const ownership = ownershipAusFormular(formFields);
    // Neu angelegte Haushaltsmitglieder erben die Wohnanschrift des Hauptkontakts.
    const householdAddress = {
      street: f("street") || undefined,
      postalCode: (f("postalcode") || f("postalCode") || f("plz")) || undefined,
      city: (f("city") || f("ort")) || undefined,
      ortsteil: (f("cityArea") || f("ortsteil")) || undefined,
      country: (f("country") || f("land")) || undefined,
    };
    const lastname = (f("lastname") || "").trim();
    const householdGroupName = `Haushalt${lastname ? " " + lastname : ""}`;
    const collections = collectedRelations();
    if (sync) {
      const report = await syncCollectedRelations(
        contactId,
        collections,
        parseArr(formFields.rel_previousIds) as unknown as number[],
        ownership,
        { householdAddress, householdGroupName },
      );
      // Ein 403/404 ist keine Exception — ohne diese Meldung sähe der Anwender
      // eine erfolgreiche Speicherung, obwohl Beziehungen unverändert blieben.
      if (report.failures.length) {
        await hinweisen({
          titel: "Nicht alle Beziehungen übernommen",
          text: "Der Kontakt ist gespeichert, aber diese Beziehungen konnten nicht übernommen werden:\n\n" + report.failures.join("\n"),
          ton: "danger",
        });
      }
    } else {
      await persistCollectedRelations(contactId, collections, ownership, {
        householdAddress,
        householdGroupName,
      });
    }
    const kv = f("rel_kvGroupId");
    if (kv) await assignKundenverbindung(contactId, Number(kv));
  };

  const [angelegteKontaktId, setAngelegteKontaktId] = React.useState<number | null>(null);
  const runSubmit = async () => {
    if (await speichernWegenVollanlageBlockiert(offeneVollanlagen)) return;
    setIsSubmitting(true);
    try {
      const result = (isApProduktpartner ? angelegteKontaktId : null) ?? await onSave();
      if (typeof result !== "number") return;
      if (isApProduktpartner && !convert) setAngelegteKontaktId(result);
      await uploadPendingPhoto(result);
      // Beim Umwandeln sind die Sektionen mit den BESTEHENDEN Beziehungen
      // vorausgefüllt — hier wird abgeglichen (PATCH/POST/DELETE) statt blind
      // neu angelegt, sonst stünde jede Beziehung anschließend doppelt da.
      await persistRelations(result, !!convert);
      // Wer den Kontakt gebracht hat: Beziehung zum Empfehlungsgeber/Tippgeber
      // und auf Wunsch dessen Status. Scheitert etwas, bleibt der Kontakt
      // angelegt — der Hinweis sagt, was nachzutragen ist.
      if (!convert) {
        const probleme = await herkunftVerknuepfen(result, formFields, contactOrigins);
        if (probleme.length) {
          await hinweisen({
            titel: "Kontakt gespeichert – bitte nachtragen",
            text: "Der Kontakt ist gespeichert, aber:\n\n" + probleme.join("\n"),
            ton: "danger",
          });
        }
      }
      // Der Arbeitgeber ist eine Beziehung, kein Kontaktfeld — und er muss NACH
      // den Beziehungen laufen: Wurde dieselbe Firma oben als Verbindung
      // erfasst, markiert der Aufruf genau diese Zeile, statt eine zweite
      // anzulegen. Ein Fehler hier darf den bereits angelegten Kontakt nicht
      // verschlucken.
      let finalArbeitgeberId = arbeitgeberId ? Number(arbeitgeberId) : undefined;
      if (!finalArbeitgeberId && arbeitgeberDraft.isNew && arbeitgeberDraft.ref && refHasName(arbeitgeberDraft.ref)) {
        try {
          // FO (auch mehrere), BO und persönlicher Inhouse-Kontakt — dieselbe
          // Verantwortlichkeit wie der Hauptkontakt (User 28.09.2026).
          const ownership = ownershipAusFormular(formFields);
          finalArbeitgeberId = await createContactFromRef(arbeitgeberDraft.ref, ownership);
        } catch (err) {
          console.error("[Anlage] Neuer Arbeitgeber konnte nicht angelegt werden:", err);
        }
      }
      if (finalArbeitgeberId && !arbeitgeberId) {
        // Rollen, die der Anwender am gespiegelten neuen Arbeitgeber gepflegt hat.
        const fehler = await arbeitgeberEintragNachreichen(
          result,
          finalArbeitgeberId,
          parseArr(formFields.rel_personFirma) as PersonFirmaItem[],
        );
        if (fehler.length) {
          await hinweisen({
            titel: "Kontakt gespeichert – bitte nachtragen",
            text: "Der Kontakt ist gespeichert, aber:\n\n" + fehler.join("\n"),
            ton: "danger",
          });
        }
      }
      if (finalArbeitgeberId && !arbeitgeberId && arbeitgeberDraft.ref) {
        // Geschäftliche Kanäle, die der neuen Firma nur dem Namen nach
        // zugeordnet waren, bekommen jetzt ihre Id (P8).
        const fehler = await firmenbezugNachreichen(result, {
          id: finalArbeitgeberId,
          name: String(arbeitgeberDraft.ref.lastName ?? ""),
        });
        for (const meldung of fehler) console.error("[Anlage]", meldung);
      }
      if (finalArbeitgeberId) {
        try {
          await arbeitgeberSetzen(result, finalArbeitgeberId);
        } catch (err) {
          console.error("[Anlage] Arbeitgeber konnte nicht verknüpft werden", err);
          await hinweisen({
            titel: "Arbeitgeber nicht verknüpft",
            text: "Der Kontakt ist gespeichert, der Arbeitgeber konnte aber nicht verknüpft werden. Bitte in der Kundenakte im Beruf-Block nachtragen.",
            ton: "danger",
          });
        }
      }
      // Dateien an den Dokumenteinträgen (Ausweiskopie, Führerschein, Urkunden).
      for (const problem of await dokDateien.ablegen(result, documents)) {
        console.error("[Anlage]", problem);
      }
      for (const ef of pendingEinladungFilesRef.current) {
        try {
          await documentService.upload({
            file: ef,
            contactId: result,
            category: "Einladung",
          });
        } catch (docErr) {
          console.error("[Anlage] Einladung konnte nicht als Dokument abgelegt werden:", docErr);
        }
      }
      for (const hf of pendingHochzeitFilesRef.current.values()) {
        try {
          await documentService.upload({
            file: hf,
            contactId: result,
            category: "Hochzeit",
          });
        } catch (docErr) {
          console.error("[Anlage] Hochzeitsdokument konnte nicht als Dokument abgelegt werden:", docErr);
        }
      }
      for (const v of verfuegungItems) {
        if (v.aktiv && v.fileRef) {
          try {
            await documentService.upload({
              file: v.fileRef,
              contactId: result,
              category: "Vollmacht",
            });
          } catch (docErr) {
            console.error("[Anlage] Verfügung-Dokument konnte nicht als Dokument abgelegt werden:", docErr);
          }
        }
      }
      for (const v of vertretungItems) {
        if (v.fileRef) {
          try {
            await documentService.upload({
              file: v.fileRef,
              contactId: result,
              category: "Vollmacht",
            });
          } catch (docErr) {
            console.error("[Anlage] Vertretung-Dokument konnte nicht als Dokument abgelegt werden:", docErr);
          }
        }
      }
      // Vereine und Mitgliedschaften verknüpfen / neu anlegen
      for (const v of vereine) {
        try {
          let vereinId = v.vereinContactId;
          if (!vereinId && v.isNew && v.ref && refHasName(v.ref)) {
            // FO (auch mehrere), BO und persönlicher Inhouse-Kontakt — dieselbe
            // Verantwortlichkeit wie der Hauptkontakt (User 28.09.2026).
            const ownership = ownershipAusFormular(formFields);
            vereinId = await createContactFromRef(v.ref, ownership);
          }
          if (vereinId) {
            const funktion = v.funktion?.trim() || (v.hatMitgliedschaft ? "Mitglied" : undefined);
            await fetch(apiUrl("contact-relation"), {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                contactSrcId: vereinId,
                contactDestId: result,
                relationType: "Mensch fürs Unternehmen",
                ...(funktion ? { function: funktion } : {}),
              }),
            });
          }
        } catch (err) {
          console.error("[Anlage] Verein/Mitgliedschaft konnte nicht verknüpft werden:", err);
        }
      }
      if (!isApProduktpartner) await vollmachtenNachAnlageMelden(result, vollmachten);
      // Interessent + NKG auf „Ja" → NKG-Vorgang anlegen (optional, blockiert nicht).
      if (!convert && isInteressent && nkgWanted) {
        try {
          await createNkgVorgang(result);
        } catch {
          /* NKG-Vorgang optional — Anlage nicht blockieren */
        }
      }
      onSaveSuccess(result);
    } catch (error) {
      await hinweisen({ titel: "Speichern nicht abgeschlossen", text: error instanceof Error ? error.message : "Bitte erneut versuchen. Bereits angelegte Person bleibt erhalten.", ton: "danger" });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Bearbeiten-Modus: die Nachläufe, die sonst `runSubmit` erledigt — Dateien
  // ablegen und einen neu eingetippten Arbeitgeber anlegen. Die Akte ruft das
  // nach ihrem Speichern auf (siehe utils/anlageNachlauf.ts). Bei jedem
  // Rendern neu hinterlegt, damit die Funktion den aktuellen Stand sieht.
  if (bearbeiten?.nachlauf) {
    const neuerArbeitgeber =
      arbeitgeberDraft.isNew && arbeitgeberDraft.ref && refHasName(arbeitgeberDraft.ref) ? arbeitgeberDraft.ref : null;
    bearbeiten.nachlauf.current = {
      arbeitgeberNeu: !!neuerArbeitgeber,
      ausfuehren: async (id, ownership) => {
        const probleme: string[] = [];
        const ablegen = async (file: File, category: string, was: string) => {
          try {
            await documentService.upload({ file, contactId: id, category });
          } catch {
            probleme.push(`${was} „${file.name}" konnte nicht abgelegt werden.`);
          }
        };
        probleme.push(...(await dokDateien.ablegen(id, documents)));
        for (const ef of pendingEinladungFilesRef.current) await ablegen(ef, "Einladung", "Die Einladung");
        for (const hf of pendingHochzeitFilesRef.current.values()) await ablegen(hf, "Hochzeit", "Das Hochzeitsdokument");
        for (const v of verfuegungItems) {
          if (v.aktiv && v.fileRef) await ablegen(v.fileRef, "Vollmacht", "Das Dokument zur Verfügung");
        }
        for (const v of vertretungItems) {
          if (v.fileRef) await ablegen(v.fileRef, "Vollmacht", "Das Dokument zur Vertretung");
        }
        // Abgelegt ist abgelegt — ein zweites Sperren darf nichts doppelt ablegen.
        pendingEinladungFilesRef.current = [];
        pendingHochzeitFilesRef.current = new Map();
        if (neuerArbeitgeber) {
          try {
            const neuId = await createContactFromRef(neuerArbeitgeber, ownership);
            if (!neuId) throw new Error("keine Id");
            probleme.push(
              ...(await arbeitgeberEintragNachreichen(id, neuId, parseArr(formFields.rel_personFirma) as PersonFirmaItem[])),
            );
            await arbeitgeberSetzen(id, neuId);
            probleme.push(
              ...(await firmenbezugNachreichen(id, { id: neuId, name: String(neuerArbeitgeber.lastName ?? "") })),
            );
          } catch {
            probleme.push(`Der neue Arbeitgeber „${String(neuerArbeitgeber.lastName ?? "")}" konnte nicht angelegt oder verknüpft werden.`);
          }
        }
        return probleme;
      },
    };
  }

  const handleSubmit = async () => {
    if (isSubmitting || bearbeiten?.nurLesen) return;
    const profilFehler = isApProduktpartner ? validatePartnerProfile(f) : [];
    if (profilFehler.length) {
      await hinweisen({ titel: "Pflichtangaben fehlen", text: profilFehler.join("\n"), ton: "danger" });
      return;
    }
    // Eine aus einer Schnellanlage geöffnete Vollanlage ist noch offen: Ihr
    // Kontakt stünde hier noch als Schnellanlage und entstünde doppelt.
    if (await speichernWegenVollanlageBlockiert(offeneVollanlagen)) return;
    // Bearbeiten-Modus: Der Aufrufer speichert (Diff-PUT, Beziehungsabgleich,
    // Arbeitgeber). Die Nachläufe der Anlage (Uploads, Vereine, Herkunft,
    // NKG, Vollmachten) gehören nicht hierher.
    if (bearbeiten) {
      setIsSubmitting(true);
      try {
        const id = await onSave();
        if (id !== false) onSaveSuccess(id);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }
    // Beim Umwandeln ist die Maske vorausgefüllt: Was hier fehlt, wird gelöscht.
    // Das betrifft auch Beziehungen, deren Rolle die Umwandlung entwertet hat —
    // sie kommen bewusst ohne Bezeichnung zurück und verschwinden, wenn keine
    // vergeben wird. Ohne Nachfrage fiele das erst später auf.
    if (convert) {
      const removed = describeRemovedRelations(
        collectedRelations(),
        parseArr(formFields.rel_previousIds) as unknown as number[],
        parseLabels(formFields.rel_previousLabels),
      );
      if (removed.length) {
        setRemovalPrompt(removed);
        return;
      }
    }
    await runSubmit();
  };

  // ── Klapp-Rahmen ──────────────────────────────────────────────────────
  const kurzfassungen: Record<string, React.ReactNode> = {
    "Anschrift (Erstwohnsitz)": anschriftKurz(f("street"), f("postalcode"), f("city"), extraAddresses.length),
    Kommunikation: kommunikationKurz(
      [f("mobilePrivate"), f("phonePrivate"), f("mobileWork"), f("phoneWork"), ...weitereWege.filter((w) => w.type !== "email").map((w) => w.value)],
      [f("emailPrivate"), f("emailWork"), ...weitereWege.filter((w) => w.type === "email").map((w) => w.value)],
    ),
    Bank: bankKurz(f("iban"), f("bankname"), extraBanks.length),
    Haushalt: anzahlKurz(parseArr(formFields.rel_household).length, "Person", "Personen"),
    "Beziehungen zu Personen": anzahlKurz(parseArr(formFields.rel_weitere).length, "Person", "Personen"),
    "Verbindungen zu Firmen und Vereinen": anzahlKurz(
      parseArr(formFields.rel_personFirma).length,
      "Verbindung",
      "Verbindungen",
    ),
  };
  // In der Akte starten leere Karten zugeklappt (User 30.09.2026); bei der
  // Neuanlage bleibt alles offen. Nur der erste Stand zählt (useKartenKlapp).
  const klapp = useKartenKlapp({
    kurzfassungen,
    fehlt: { "Allgemeine Informationen": !requiredOk },
    startZu: bearbeiten ? leereKarten(formFields, PRIVAT_KARTEN, kurzfassungen) : undefined,
  });
  const speichern = () => void handleSubmit();

  // Was noch fehlt — für Warndreieck und Popup der Speichern-Leiste.
  const maengel: AnlageMangel[] = [
    ...(!requiredOk
      ? [{
          hinweis: "Bitte Pflichtfelder (*) ausfüllen",
          felder: [
            ...(!f("lastname").trim() ? ["Nachname"] : []),
            ...(!f("firstname").trim() ? ["Vorname"] : []),
          ],
        }]
      : []),
    // Im Bearbeiten-Modus stammen Formatfehler oft aus dem Bestand — Hinweis,
    // aber keine Sperre (sonst ließe sich die Akte nicht mehr speichern).
    ...(!formatValid
      ? [{ hinweis: "Bitte markierte Felder korrigieren", sperrt: !bearbeiten }]
      : []),
  ];

  // ── Weitere Anschriften / Banken ──────────────────────────────────────
  // Hinzufügen, Fertig und Entfernen erledigt die EintragsListe; hier landet
  // nur der fertige Stand — im lokalen State UND als JSON im Formular.
  const setzeWeitereAnschriften = (next: WeitereAnschrift[]) => {
    setExtraAddresses(next);
    syncJson("additionalAddresses", next);
  };
  const setzeWeitereBanken = (next: WeitereBank[]) => {
    setExtraBanks(next);
    syncJson("additionalBanks", next);
  };

  // ── Bank-Repeater ─────────────────────────────────────────────────────
  // --- Bankdaten aus der IBAN ---------------------------------------------
  // Sobald die IBAN eine deutsche Bankleitzahl trägt, stehen BIC und
  // Kreditinstitut fest. Eingetragen wird nur in LEERE Felder — eine bewusst
  // abweichende Eingabe (etwa nach einer Bankfusion) darf nicht stillschweigend
  // korrigiert werden. Dasselbe Muster nutzt schon die Steuernummer, die das
  // Finanzamt vorbelegt.
  const bankUebernehmen = (bank: BankTreffer) => {
    const leer = (v: unknown) => !String(v ?? "").trim();
    if (bank.bic && leer(f("bic"))) set("bic", bank.bic);
    if (leer(f("bankname"))) set("bankname", bank.bankName);
  };

  // Die weiteren Geburtsangaben stehen zugeklappt: Sie stehen auf der
  // Geburtsurkunde und werden selten gebraucht — aber wer sie hat, will sie
  // nicht in einem Bemerkungsfeld ablegen. Sind sie gefüllt (Umwandlung,
  // Wiedereintritt in die Maske), ist der Block offen.
  const [geburtMehrOffen, setGeburtMehrOffen] = React.useState<boolean>(
    () =>
      Boolean(
        String(formFields.birthTime ?? "").trim() ||
          String(formFields.birthWeightGrams ?? "").trim() ||
          String(formFields.birthHeightCm ?? "").trim() ||
          String(formFields.birthCertificateUrl ?? "").trim(),
      ),
  );

  React.useEffect(() => {
    // Im Bearbeiten-Modus nichts vorbelegen: Der Wert stünde sonst im Ladestand
    // der Akte, würde angezeigt, aber nie gespeichert.
    if (!formFields.countryofbirth && !convert && !bearbeiten) {
      updateField("countryofbirth", "Deutschland");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Die angekreuzten Verfügungen liegen als JSON im Formularfeld — wie die
  // Beziehungslisten auch. Erst der POST macht daraus Zeilen.
  const verfuegungen = parseArr(formFields.verfuegungen) as VerfuegungForm[];


  const rufnummernFuerModal: ErreichbarkeitRufnummerItem[] = [];

  if (f("phonePrivate")?.trim()) {
    rufnummernFuerModal.push({
      id: "phonePrivate",
      label: "Telefon (privat)",
      number: f("phonePrivate"),
      erreichbarkeit: erreichbarkeiten["phonePrivate"] || "",
      onChange: (v) => setErreichbarkeitField("phonePrivate", v),
    });
  }
  if (f("mobilePrivate")?.trim()) {
    rufnummernFuerModal.push({
      id: "mobilePrivate",
      label: "Mobil (privat)",
      number: f("mobilePrivate"),
      erreichbarkeit: erreichbarkeiten["mobilePrivate"] || "",
      onChange: (v) => setErreichbarkeitField("mobilePrivate", v),
    });
  }
  if (f("phoneWork")?.trim()) {
    const comp = firmenText(workFirmen("phoneWork"));
    rufnummernFuerModal.push({
      id: "phoneWork",
      label: comp ? `Telefon (${comp})` : "Telefon (geschäftlich)",
      number: f("phoneWork"),
      erreichbarkeit: erreichbarkeiten["phoneWork"] || "",
      onChange: (v) => setErreichbarkeitField("phoneWork", v),
    });
  }
  if (f("mobileWork")?.trim()) {
    const comp = firmenText(workFirmen("mobileWork"));
    rufnummernFuerModal.push({
      id: "mobileWork",
      label: comp ? `Mobil (${comp})` : "Mobil (geschäftlich)",
      number: f("mobileWork"),
      erreichbarkeit: erreichbarkeiten["mobileWork"] || "",
      onChange: (v) => setErreichbarkeitField("mobileWork", v),
    });
  }
  extraCommunications
    .filter((c) => (c.type === "phone" || c.type === "mobile" || c.type === "fax") && c.value?.trim())
    .forEach((c) => {
      const isPrivat = !c.zuordnung || c.zuordnung === "privat";
      const baseType = c.type === "mobile" ? "Mobil" : c.type === "fax" ? "Fax" : "Telefon";
      const fullLabel = isPrivat
        ? getPlainCommLabel(baseType, undefined, c.label) + " (privat)"
        : getPlainCommLabel(baseType, firmenText(extraFirmen(c)), c.label) + " (geschäftlich)";
      rufnummernFuerModal.push({
        id: c.id,
        label: fullLabel,
        number: c.value,
        erreichbarkeit: c.erreichbarkeit || "",
        onChange: (v) => updateExtraComm(c.id, { erreichbarkeit: v }),
      });
    });

  // „+ Weitere Informationen erfassen" in den Schnellanlagen dieser Maske:
  // FO/BO, Wohnanschrift (für Haushaltsmitglieder) und — im Bearbeiten-Modus
  // der Akte — die Id dieses Kontakts gehen in die Vollanlage mit.
  const elternId = bearbeiten?.contactId ?? convert?.contactId;
  const vollanlageEltern = React.useMemo<VollanlageEltern>(
    () => ({
      elternId,
      elternName: [str(formFields.lastname).trim(), str(formFields.firstname).trim()].filter(Boolean).join(", ") || undefined,
      ownership: ownershipAusFormular(formFields),
      haushaltsAnschrift: {
        street: str(formFields.street) || undefined,
        postalCode: str(formFields.postalcode) || undefined,
        city: str(formFields.city) || undefined,
        ortsteil: str(formFields.cityArea) || undefined,
        country: str(formFields.country) || undefined,
      },
    }),
    [formFields, elternId],
  );

  return (
    <OffeneVollanlagenContext.Provider value={offeneVollanlagen}>
    <VollanlageElternContext.Provider value={vollanlageEltern}>
    <KartenKlappContext.Provider value={klapp}>
    <div className={s.page}>
      <div className={s.scroll}>
        {angelegteKontaktId && <p role="status">Die Person ist bereits gespeichert. Erneutes Speichern wiederholt nur noch ausstehende Schritte. Weitere Daten können anschließend in der Akte geändert werden.</p>}
        <fieldset disabled={!!angelegteKontaktId} className={mergeClasses(s.inner, bearbeiten && s.innerEingebettet)} style={{ border: 0, minWidth: 0 }}>
          {!bearbeiten && (
            <h1 className={s.pageTitle}>
              {convert ? "Zu Person umwandeln" : "Privatkontakt anlegen"}
            </h1>
          )}
          {convert && <ConvertBanner from={convert.from} relations={convert.relationsWithoutRole} fields={convert.fieldsToClear} />}
          {/* 1 — Kontaktstatus (User 26.09.2026): Status, bei „Sonstige" Bereich |
              Rolle, Firma | Privat — und die Folgefelder der Rollen inline. Deren
              Einträge stehen unten in den Beziehungskarten noch einmal. */}
          {!bearbeiten && (
          <SectionCard title="Kontaktstatus">
            <KontaktStatusSchritt
              f={f}
              set={set}
              typ="person"
              onTyp={onTypWechsel ? (t) => t === "firma" && onTypWechsel("firma") : undefined}
              convert={!!convert}
              coIntern={hasPermission("cointern:read")}
              folgefelder={(folgen) => (
                <>
                  {folgen.includes("person") && (
                    <PersonenMitBeziehungInline
                      haushalt={parseArr(formFields.rel_household) as HouseholdItem[]}
                      weitere={parseArr(formFields.rel_weitere) as HouseholdItem[]}
                      onHaushalt={(n) => set("rel_household", JSON.stringify(n))}
                      onWeitere={(n) => set("rel_weitere", JSON.stringify(n))}
                      startImHaushalt={gewaehlteRollen.includes("Haushaltsmitglied")}
                      rollen={gewaehlteRollen}
                    />
                  )}
                  {(folgen.includes("unternehmen") || folgen.includes("verein")) && (
                    <UnternehmenInline
                      value={parseArr(formFields.rel_personFirma) as PersonFirmaItem[]}
                      onChange={(n) => set("rel_personFirma", JSON.stringify(n))}
                      verein={!folgen.includes("unternehmen")}
                      rollen={gewaehlteRollen}
                    />
                  )}
                </>
              )}
            >
              <DuplicateWarningPanel results={dup.results} loading={dup.loading} />
            </KontaktStatusSchritt>
          </SectionCard>
          )}

          {/* 2 — Allgemeine Informationen (heißt in der Kundenakte genauso;
              vorher „Stammdaten / Persönliche Angaben“) */}
          {isApProduktpartner && <AnsprechpartnerFelder f={f} set={set} partnerArt={gewaehlteRollen[0] === "Ansprechpartner Kooperationspartner" ? "Kooperationspartner" : "Produktpartner"} nurLesen={bearbeiten?.nurLesen || bearbeiten?.gesperrt || !!angelegteKontaktId} />}
          <SectionCard title="Allgemeine Informationen">
            {/* Visitenkarte zuerst: Sie fuellt Anrede, Name, Position,
                Anschrift und die geschaeftlichen Kontaktwege vor. Deshalb
                steht der Knopf am Anfang des Blocks. Im Umwandlungs-Modus
                gibt es nichts anzulegen — dort entfaellt er. */}
            {!convert && !bearbeiten && (
              <FieldRow columns={1}>
                <div>
                  <button
                    type="button"
                    className={s.scanBtn}
                    title="Vorder- und Rueckseite abfotografieren; die erkannten Angaben werden vor der Uebernahme angezeigt"
                    onClick={() => setScanModus("alle")}
                  >
                    <IconVisitenkarte /> Datei scannen zur vereinfachten Kontaktanlage
                  </button>
                </div>
              </FieldRow>
            )}
            <FieldRow columns={2}>
              <LabeledSelect label="Anrede" name="anrede" value={f("anrede")} onChange={(v) => set("anrede", v)} options={anredeOptionen.map((o) => ({ value: o.salutation, label: o.salutation }))} placeholder="Auswählen" />
              <LabeledSelect label="Titel" name="title" value={f("title")} onChange={(v) => set("title", v)} options={TITLE_OPTIONS} placeholder="Auswählen" />
            </FieldRow>
            <FieldRow columns={2}>
              {/* Sternchen und Leer-Meldung nur in der Anlage: im Umwandlungs-Modus
                  ist nichts pflichtig (bestehender Kontakt, kein Anlagevorgang). */}
              <LabeledInput label="Nachname" name="lastname" required={!convert} invalid={!convert && empty("lastname")} errorText="Nachname ist erforderlich." value={f("lastname")} onChange={(v) => set("lastname", v)} placeholder="Mustermann" />
              <LabeledInput label="Vorname (gemäß Personalausweis)" name="firstname" required={!convert} invalid={!convert && empty("firstname")} errorText="Vorname ist erforderlich." value={f("firstname")} onChange={(v) => set("firstname", v)} placeholder="Max" />
            </FieldRow>
            <FieldRow columns={2}>
              <LabeledInput label="Rufname" name="nickname" value={f("nickname")} onChange={(v) => set("nickname", v)} placeholder="Maxi" />
              <NationalitySelect label="Staatsangehörigkeit" name="citizenship" value={f("citizenship")} onChange={(v) => set("citizenship", v)} />
            </FieldRow>

            {/* Trennlinie zu den Geburtsangaben */}
            <div style={{ margin: "14px 0", borderTop: `1px solid ${REFINED.line}` }} />
            <FieldRow columns={2}>
              <LabeledInput
                label="Geburtsdatum"
                name="dateofbirth"
                type="date"
                value={f("dateofbirth")}
                onChange={(v) => set("dateofbirth", v)}
              />
              {!isApProduktpartner && <LabeledInput
                label="Geburtsname"
                name="birthname"
                value={f("birthname")}
                onChange={(v) => set("birthname", v)}
              />}
            </FieldRow>
            {!isApProduktpartner && <><FieldRow columns={2}>
              <CountrySelect
                label="Geburtsland"
                name="countryofbirth"
                value={f("countryofbirth") || "Deutschland"}
                onChange={(v) => set("countryofbirth", v)}
              />
              <CityAutocomplete
                label="Geburtsort"
                name="placeofbirth"
                value={f("placeofbirth")}
                onChange={(v) => set("placeofbirth", v)}
                land={f("countryofbirth") || "Deutschland"}
              />
            </FieldRow>

            {/* Weitere Geburtsangaben — ein- und ausklappbar */}
            {geburtMehrOffen ? (
              <>
                <FieldRow columns={2}>
                  <LabeledInput
                    label="Geburtsgröße (cm)"
                    name="birthHeightCm"
                    type="number"
                    value={f("birthHeightCm")}
                    onChange={(v) => set("birthHeightCm", v)}
                    placeholder="52"
                  />
                  <LabeledInput
                    label="Geburtsgewicht (g)"
                    name="birthWeightGrams"
                    type="number"
                    value={f("birthWeightGrams")}
                    onChange={(v) => set("birthWeightGrams", v)}
                    placeholder="3480"
                  />
                </FieldRow>
                <FieldRow columns={2}>
                  <LabeledInput
                    label="Geburtszeit"
                    name="birthTime"
                    type="time"
                    value={f("birthTime")}
                    onChange={(v) => set("birthTime", v)}
                  />
                  <div />
                </FieldRow>
                <div style={{ marginTop: "10px", marginBottom: "8px" }}>
                  <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
                    <div>
                      <label style={{ fontSize: "0.84rem", fontWeight: 700, color: REFINED.ink, marginBottom: "6px", display: "block" }}>
                        Geburtsurkunde
                      </label>
                      <div className="sza-zeile">
                        {geburtsurkundeUrl && (
                          <>
                            <button
                              type="button"
                              className="sza-btn sza-datei"
                              onClick={() => void oeffneAuthentifiziert(geburtsurkundeUrl.replace(/^\/+/, ""))}
                              title={`Geburtsurkunde öffnen: ${geburtsurkundeName || "Geburtsurkunde"}`}
                            >
                              {DOKUMENT_ICON}
                              <span className="sza-dateiname">{geburtsurkundeName || "Geburtsurkunde"}</span>
                            </button>
                            <button
                              type="button"
                              className="sza-btn sza-entfernen"
                              onClick={handleRemoveGeburtsurkunde}
                              title="Geburtsurkunde entfernen"
                              aria-label="Geburtsurkunde entfernen"
                            >
                              {ENTFERNEN_ICON}
                            </button>
                          </>
                        )}
                        <button
                          type="button"
                          className="sza-btn"
                          onClick={() => setScanModus("geburtsurkunde")}
                          title="Geburtsurkunde scannen — Geburtsdatum, -zeit, -ort und Registernummer übernehmen, Datei am Dokumenteintrag ablegen"
                        >
                          {UPLOAD_ICON} Geburtsurkunde scannen
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      className={s.ghostBtn}
                      onClick={() => setGeburtMehrOffen(false)}
                      title="Weitere Geburtsangaben einklappen"
                    >
                      ↑ Weitere Geburtsangaben einklappen
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className={s.rowActions}>
                <button
                  type="button"
                  className={s.ghostBtn}
                  onClick={() => setGeburtMehrOffen(true)}
                >
                  + Weitere Geburtsangaben (Zeit, Gewicht, Größe, Urkunde)
                </button>
              </div>
            )}

            </>}

            {/* Verstorben — auch bei der Anlage, denn es kommt vor: Ein
                Erblasser wird erfasst, damit Erben und Verträge an ihm hängen
                können. Der Schalter führt, das Datum ist optional: Meist
                erfährt man vom Tod über Dritte und kennt das Datum nicht, und
                ein erfundenes sähe später aus wie eine Tatsache. */}
            <div style={{ marginTop: "14px", paddingTop: "12px", borderTop: `1px solid ${REFINED.line}` }}>
              <FieldRow columns={2}>
                <LabeledSelect
                  label="Verstorben"
                  name="deceased"
                  value={f("deceased") === "ja" ? "ja" : ""}
                  onChange={(v) => {
                    set("deceased", v);
                    // „Nein" räumt das Datum mit weg — sonst bliebe ein
                    // Sterbedatum an einer Person stehen, die als lebend geführt
                    // wird, und das Kreuz käme über den Datums-Rückfall zurück.
                    if (v !== "ja") set("deceasedAt", "");
                  }}
                  options={[{ value: "ja", label: "Ja" }]}
                  placeholder="Nein"
                />
                {f("deceased") === "ja" && (
                  <LabeledInput
                    label="Verstorben am (wenn bekannt)"
                    name="deceasedAt"
                    type="date"
                    value={f("deceasedAt")}
                    onChange={(v) => set("deceasedAt", v)}
                  />
                )}
              </FieldRow>
            </div>
            {/* Die Belege zum Todesfall (T-357) — Sterbeanzeige, Sterbeurkunde,
                Kondolenzschreiben, Danksagung … als Liste, schon beim Anlegen.
                Ein neuer Upload hängt an, er ersetzt nie; Entfernen fragt nach.
                Anders als das Datum werden sie beim Zurückschalten auf „Nein"
                NICHT geleert — der Payload-Builder lässt sie ohne Flag
                ohnehin weg, und wer versehentlich umschaltet, soll seine
                Uploads nicht verlieren. */}
            {f("deceased") === "ja" && (
              <FieldRow columns={1}>
                <div className="sza-feld">
                  <span className="sza-label" id="priv-todesfall-belege-label">
                    Belege zum Todesfall
                  </span>
                  <ContactTodesfallBelege
                    value={todesfallBelegeAusFeld(f("todesfallBelege"))}
                    editable={!bearbeiten?.nurLesen && !bearbeiten?.gesperrt}
                    inputId="priv-todesfall-belege-datei"
                    onChange={(next) => set("todesfallBelege", JSON.stringify(next))}
                    // Wie die übrigen Fehler in dieser Maske: sichtbar, nicht
                    // nur in der Konsole. Ein stillschweigend fehlgeschlagener
                    // Upload sähe aus wie ein erfolgreicher.
                    onFehler={(m) => void hinweisen({ titel: "Upload fehlgeschlagen", text: m, ton: "danger" })}
                  />
                </div>
              </FieldRow>
            )}
          </SectionCard>

          {/* 3 — Herkunft */}
          {/* Herkunft gehört zur Anlage — beim Umwandeln eines bestehenden
              Kontakts ist die Frage sinnlos, und ein Ansprechpartner bei einer
              Gesellschaft wird nicht „gewonnen". */}
          {!convert && !isApProduktpartner && (
            <SectionCard title="Wie kommt der Kontakt zustande?">
              <HerkunftAbschnitt
                isInteressent={isInteressent}
                auswahl={contactOrigins}
                toggle={toggleContactOrigin}
                sonstiges={contactOriginOther}
                onSonstiges={setContactOriginOtherValue}
                formFields={formFields}
                set={set}
              />
            </SectionCard>
          )}

          {/* 5 — Anschrift */}
          <SectionCard title="Anschrift (Erstwohnsitz)">
            {/* Das Land steht VOR der Adresse: es entscheidet, wie der Rest zu
                lesen ist (PLZ-Format, Reihenfolge). Default Deutschland. */}
            <FieldRow columns={2}>
              <CountrySelect value={f("country") || "Deutschland"} onChange={(v) => set("country", v)} />
            </FieldRow>
            <FieldRow columns={2}>
              <AddressAutocomplete label="Straße und Hausnummer" name="street" value={f("street")} onChange={(v) => set("street", v)} land={f("country") || "Deutschland"} rightSlot={<KartenKnopf anschrift={{ strasse: f("street"), plz: f("postalcode"), ort: f("city"), land: f("country") }} />} onResolved={(a) => { set("street", a.street); set("postalcode", a.postalcode); set("city", a.city); set("cityArea", a.cityArea); }} />
              <LabeledInput label="Postleitzahl" name="postalcode" value={f("postalcode")} onChange={(v) => setPlzOderOrt("postalcode", v)}
                invalid={!plzPruefen(f("postalcode"), f("country") || "Deutschland").ok}
                errorText={plzPruefen(f("postalcode"), f("country") || "Deutschland").meldung}
                placeholder={plzPruefen("", f("country") || "Deutschland").beispiel} />
            </FieldRow>
            <FieldRow columns={2}>
              <LabeledInput label="Ort" name="city" value={f("city")} onChange={(v) => setPlzOderOrt("city", v)} placeholder={PLACEHOLDERS.city} />
              <LabeledInput label="Ortsteil" name="cityArea" value={f("cityArea")} onChange={(v) => set("cityArea", v)} placeholder="Sendling" />
            </FieldRow>
            {/* Weitere Anschriften — gleiche Felder wie oben, nur mit Art.
                Eine Ferienimmobilie ist KEIN Zweitwohnsitz: anderer Zweck,
                andere Risiken — und man kann mehrere haben. */}
            <EintragsListe<WeitereAnschrift>
              items={extraAddresses}
              onChange={setzeWeitereAnschriften}
              neu={neueAnschrift}
              hinzufuegenLabel="Weitere Anschrift (Zweitwohnsitz, Ferienimmobilie, Postanschrift …)"
              istLeer={anschriftIstLeer}
              pruefen={anschriftPruefen}
              renderFelder={(a, update, { index, entwurf }) => (
                <WeitereAnschriftFelder
                  wert={a}
                  onChange={update}
                  schluessel={entwurf ? "neu" : String(index)}
                  kopf="art"
                />
              )}
            />
          </SectionCard>

          {/* 6 — Kommunikation */}
          {/* Zwei feste Spalten wie in der Kontaktakte (ContactDetailsRedesign):
              links alles Private, rechts alles Geschäftliche. Die Überschrift
              trägt die Unterscheidung, deshalb heißt das Feld nur noch
              „Telefon". Sie steht IM Spalten-Container — bricht die Karte auf
              eine Spalte um (≤600px), rutscht sie mit ihrer Gruppe nach unten
              und wiederholt sich, statt einsam über einem Feld zu stehen. */}
          <SectionCard title="Kommunikation">
            {/* Erreichbarkeit oben rechts bevor Labels und Felder */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "8px" }}>
              <button
                type="button"
                onClick={() => setErreichbarkeitModalOpen(true)}
                title={allgemeineErreichbarkeit ? `Erreichbarkeit: ${allgemeineErreichbarkeit}` : "Erreichbarkeit festlegen"}
                style={{
                  background: "none",
                  border: `1px solid ${allgemeineErreichbarkeit ? REFINED.aub : REFINED.line}`,
                  borderRadius: "8px",
                  padding: "5px 12px",
                  cursor: "pointer",
                  color: allgemeineErreichbarkeit ? REFINED.aub : REFINED.ink,
                  fontFamily: REFINED.fontUI,
                  fontSize: "0.82rem",
                  fontWeight: 600,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  backgroundColor: allgemeineErreichbarkeit ? "rgba(58, 12, 49, 0.04)" : REFINED.white,
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="8" cy="8" r="6.2" />
                  <polyline points="8 4.2 8 8 10.5 9.5" />
                </svg>
                <span>Erreichbarkeit</span>
              </button>
            </div>

            <FieldRow columns={2}>
              {KOMM_SPALTEN.map((spalte) => (
                <div
                  key={spalte}
                  className={s.commCol}
                  role="group"
                  aria-labelledby={`komm-spalte-${spalte}`}
                >
                  <div className={s.commColHead} id={`komm-spalte-${spalte}`}>
                    {KOMM_SPALTEN_TITEL[spalte]}
                  </div>
                  {spalte === "privat" ? (
                    <>
                      {/* Telefone (privat) */}
                      <PhoneInput
                        label="Telefon"
                        name="phonePrivate"
                        kind="landline"
                        value={f("phonePrivate")}
                        onChange={(v) => set("phonePrivate", v)}
                        erreichbarkeit={erreichbarkeiten["phonePrivate"]}
                        onErreichbarkeitChange={(v) => setErreichbarkeitField("phonePrivate", v)}
                        unterFeld={hauptHinweise("telefon", "privat")}
                      />
                      {extraCommunications
                        .filter((c) => (!c.zuordnung || c.zuordnung === "privat") && c.type === "phone")
                        .map((c, idx) => {
                          const baseTitle = `Telefon ${idx + 2}`;
                          return (
                            <PhoneInput
                              key={c.id}
                              label={renderCommLabel(baseTitle, undefined, c.label)}
                              fieldLabelText={getPlainCommLabel(baseTitle, undefined, c.label)}
                              name={`extra-comm-${c.id}`}
                              kind="landline"
                              value={c.value}
                              onChange={(v) => updateExtraComm(c.id, { value: v })}
                              onRemoveIfEmpty={() => removeExtraComm(c.id)}
                              unterFeld={extraHinweise(c)}
                              erreichbarkeit={c.erreichbarkeit}
                              onErreichbarkeitChange={(v) => updateExtraComm(c.id, { erreichbarkeit: v })}
                            />
                          );
                        })}

                      {/* Mobiltelefone (privat) */}
                      <PhoneInput
                        label="Mobil"
                        name="mobilePrivate"
                        kind="mobile"
                        value={f("mobilePrivate")}
                        onChange={(v) => set("mobilePrivate", v)}
                        erreichbarkeit={erreichbarkeiten["mobilePrivate"]}
                        onErreichbarkeitChange={(v) => setErreichbarkeitField("mobilePrivate", v)}
                        unterFeld={hauptHinweise("mobil", "privat")}
                      />
                      {extraCommunications
                        .filter((c) => (!c.zuordnung || c.zuordnung === "privat") && c.type === "mobile")
                        .map((c, idx) => {
                          const baseTitle = `Mobil ${idx + 2}`;
                          return (
                            <PhoneInput
                              key={c.id}
                              label={renderCommLabel(baseTitle, undefined, c.label)}
                              fieldLabelText={getPlainCommLabel(baseTitle, undefined, c.label)}
                              name={`extra-comm-${c.id}`}
                              kind="mobile"
                              value={c.value}
                              onChange={(v) => updateExtraComm(c.id, { value: v })}
                              onRemoveIfEmpty={() => removeExtraComm(c.id)}
                              unterFeld={extraHinweise(c)}
                              erreichbarkeit={c.erreichbarkeit}
                              onErreichbarkeitChange={(v) => updateExtraComm(c.id, { erreichbarkeit: v })}
                            />
                          );
                        })}

                      {/* Fax (privat, falls erfasst) */}
                      {extraCommunications
                        .filter((c) => (!c.zuordnung || c.zuordnung === "privat") && c.type === "fax")
                        .map((c, idx, arr) => {
                          const baseTitle = arr.length > 1 ? `Fax ${idx + 1}` : "Fax";
                          return (
                            <PhoneInput
                              key={c.id}
                              label={renderCommLabel(baseTitle, undefined, c.label)}
                              fieldLabelText={getPlainCommLabel(baseTitle, undefined, c.label)}
                              name={`extra-comm-${c.id}`}
                              kind="fax"
                              value={c.value}
                              onChange={(v) => updateExtraComm(c.id, { value: v })}
                              onRemoveIfEmpty={() => removeExtraComm(c.id)}
                              unterFeld={extraHinweise(c)}
                            />
                          );
                        })}

                      {/* E-Mail-Adressen (privat) */}
                      <EmailInput
                        label="E-Mail"
                        name="emailPrivate"
                        contactId={eigeneKontaktId}
                        value={f("emailPrivate")}
                        onChange={(v) => set("emailPrivate", v)}
                        unterFeld={hauptHinweise("email", "privat")}
                      />
                      {extraCommunications
                        .filter((c) => (!c.zuordnung || c.zuordnung === "privat") && c.type === "email")
                        .map((c, idx) => {
                          const baseTitle = `E-Mail ${idx + 2}`;
                          return (
                            <EmailInput
                              key={c.id}
                              label={renderCommLabel(baseTitle, undefined, c.label)}
                              name={`extra-comm-${c.id}`}
                              contactId={eigeneKontaktId}
                              value={c.value}
                              onChange={(v) => updateExtraComm(c.id, { value: v })}
                              onRemoveIfEmpty={() => removeExtraComm(c.id)}
                              unterFeld={extraHinweise(c)}
                            />
                          );
                        })}
                    </>
                  ) : (
                    <>
                      {/* Telefone (geschäftlich) */}
                      <PhoneInput
                        label={renderCommLabel("Telefon", firmaWahl(workFirmen("phoneWork"), (c) => setCommCompany("phoneWork", c)))}
                        fieldLabelText={getPlainCommLabel("Telefon", firmenText(workFirmen("phoneWork")), undefined)}
                        name="phoneWork"
                        geschaeftlich
                        zentrale={geschaeftlicheZentrale}
                        kind="landline"
                        value={f("phoneWork")}
                        onChange={(v) => set("phoneWork", v)}
                        erreichbarkeit={erreichbarkeiten["phoneWork"]}
                        onErreichbarkeitChange={(v) => setErreichbarkeitField("phoneWork", v)}
                        unterFeld={hauptHinweise("telefon", "geschaeftlich")}
                      />
                      {extraCommunications
                        .filter((c) => c.zuordnung === "geschaeftlich" && c.type === "phone")
                        .map((c, idx) => {
                          const baseTitle = `Telefon ${idx + 2}`;
                          return (
                            <PhoneInput
                              key={c.id}
                              label={renderCommLabel(baseTitle, firmaWahl(extraFirmen(c), (fa) => setExtraCommFirma(c.id, fa)), c.label)}
                              fieldLabelText={getPlainCommLabel(baseTitle, firmenText(extraFirmen(c)), c.label)}
                              name={`extra-comm-${c.id}`}
                              geschaeftlich
                              zentrale={geschaeftlicheZentrale}
                              kind="landline"
                              value={c.value}
                              onChange={(v) => updateExtraComm(c.id, { value: v })}
                              onRemoveIfEmpty={() => removeExtraComm(c.id)}
                              unterFeld={extraHinweise(c)}
                              erreichbarkeit={c.erreichbarkeit}
                              onErreichbarkeitChange={(v) => updateExtraComm(c.id, { erreichbarkeit: v })}
                            />
                          );
                        })}

                      {/* Mobiltelefone (geschäftlich) */}
                      <PhoneInput
                        label={renderCommLabel("Mobil", firmaWahl(workFirmen("mobileWork"), (c) => setCommCompany("mobileWork", c)))}
                        fieldLabelText={getPlainCommLabel("Mobil", firmenText(workFirmen("mobileWork")), undefined)}
                        name="mobileWork"
                        geschaeftlich
                        kind="mobile"
                        value={f("mobileWork")}
                        onChange={(v) => set("mobileWork", v)}
                        erreichbarkeit={erreichbarkeiten["mobileWork"]}
                        onErreichbarkeitChange={(v) => setErreichbarkeitField("mobileWork", v)}
                        unterFeld={hauptHinweise("mobil", "geschaeftlich")}
                      />
                      {extraCommunications
                        .filter((c) => c.zuordnung === "geschaeftlich" && c.type === "mobile")
                        .map((c, idx) => {
                          const baseTitle = `Mobil ${idx + 2}`;
                          return (
                            <PhoneInput
                              key={c.id}
                              label={renderCommLabel(baseTitle, firmaWahl(extraFirmen(c), (fa) => setExtraCommFirma(c.id, fa)), c.label)}
                              fieldLabelText={getPlainCommLabel(baseTitle, firmenText(extraFirmen(c)), c.label)}
                              name={`extra-comm-${c.id}`}
                              geschaeftlich
                              kind="mobile"
                              value={c.value}
                              onChange={(v) => updateExtraComm(c.id, { value: v })}
                              onRemoveIfEmpty={() => removeExtraComm(c.id)}
                              unterFeld={extraHinweise(c)}
                              erreichbarkeit={c.erreichbarkeit}
                              onErreichbarkeitChange={(v) => updateExtraComm(c.id, { erreichbarkeit: v })}
                            />
                          );
                        })}

                      {/* Fax (geschäftlich, falls erfasst) */}
                      {extraCommunications
                        .filter((c) => c.zuordnung === "geschaeftlich" && c.type === "fax")
                        .map((c, idx, arr) => {
                          const baseTitle = arr.length > 1 ? `Fax ${idx + 1}` : "Fax";
                          return (
                            <PhoneInput
                              key={c.id}
                              label={renderCommLabel(baseTitle, firmaWahl(extraFirmen(c), (fa) => setExtraCommFirma(c.id, fa)), c.label)}
                              fieldLabelText={getPlainCommLabel(baseTitle, firmenText(extraFirmen(c)), c.label)}
                              name={`extra-comm-${c.id}`}
                              geschaeftlich
                              zentrale={geschaeftlicheZentrale}
                              kind="fax"
                              value={c.value}
                              onChange={(v) => updateExtraComm(c.id, { value: v })}
                              onRemoveIfEmpty={() => removeExtraComm(c.id)}
                              unterFeld={extraHinweise(c)}
                            />
                          );
                        })}

                      {/* E-Mail-Adressen (geschäftlich) */}
                      <EmailInput
                        label={renderCommLabel("E-Mail", firmaWahl(workFirmen("emailWork"), (c) => setCommCompany("emailWork", c)))}
                        name="emailWork"
                        contactId={eigeneKontaktId}
                        value={f("emailWork")}
                        onChange={(v) => set("emailWork", v)}
                        unterFeld={hauptHinweise("email", "geschaeftlich")}
                      />
                      {extraCommunications
                        .filter((c) => c.zuordnung === "geschaeftlich" && c.type === "email")
                        .map((c, idx) => {
                          const baseTitle = `E-Mail ${idx + 2}`;
                          return (
                            <EmailInput
                              key={c.id}
                              label={renderCommLabel(baseTitle, firmaWahl(extraFirmen(c), (fa) => setExtraCommFirma(c.id, fa)), c.label)}
                              name={`extra-comm-${c.id}`}
                              contactId={eigeneKontaktId}
                              value={c.value}
                              onChange={(v) => updateExtraComm(c.id, { value: v })}
                              onRemoveIfEmpty={() => removeExtraComm(c.id)}
                              unterFeld={extraHinweise(c)}
                            />
                          );
                        })}
                    </>
                  )}
                </div>
              ))}
            </FieldRow>

            <div className={s.rowActions}>
              <button
                type="button"
                className={s.ghostBtn}
                onClick={() => setWeitereKommOpen(true)}
              >
                + Weiterer Kommunikationsweg
              </button>
            </div>
          </SectionCard>


          {/* 7 — Beruf (beim Ansprechpartner steht der Beruf in Position und
              Funktionsbereich, nicht in Arbeitgeber/Branche) */}
          {!isApProduktpartner && (
          <SectionCard title="Beruf">
            {/* Erste Zeile: Status | Arbeitgeber (beim Rentner: Status | —) */}
            <FieldRow columns={2}>
              <LabeledSelect
                label="Status"
                name="occupationStatus"
                value={f("occupationStatus")}
                onChange={(v) => {
                  for (const feld of berufFelderZuLeeren(
                    { status: f("occupationStatus"), weitere: f("weitereTaetigkeit") },
                    { status: v, weitere: istRentner(v) ? "" : f("weitereTaetigkeit") },
                  )) {
                    if (feld === "employer") arbeitgeberLeeren();
                    else set(feld, "");
                  }
                  set("occupationStatus", v);
                  if (v !== "Angestellt" && v !== "Beamter") {
                    set("zusaetzlichSelbststaendig", "");
                  }
                  if (v !== "Angestellt" && v !== "Beamter" && v !== "Selbständig/Freiberufler" && !istRentner(v)) {
                    set("position", "");
                  }
                }}
                options={[
                  { value: "Angestellt", label: "Angestellt" },
                  { value: "Beamter", label: "Beamter" },
                  { value: "Selbständig/Freiberufler", label: "Selbständig/Freiberufler" },
                  { value: RENTNER_STATUS, label: "Rentner" },
                ]}
                placeholder="Status wählen"
              />
              {berufSicht.rentnerFrage ? <div /> : arbeitgeberAuswahl}
            </FieldRow>

            {/* Rentner: kein Arbeitgeber — aber vielleicht eine weitere
                Tätigkeit (Minijob beim alten Arbeitgeber, selbstständig). */}
            {berufSicht.rentnerFrage && (
              <div style={{ display: "flex", flexDirection: "column", rowGap: "12px", marginTop: "6px" }}>
                <div role="radiogroup" aria-label="Führt der Kontakt zusätzlich eine Tätigkeit aus?" style={{ display: "flex", flexDirection: "column", rowGap: "8px" }}>
                  <span style={{ fontSize: "0.88rem", fontWeight: 600, color: REFINED.ink }}>
                    Führt der Kontakt zusätzlich eine Tätigkeit aus?
                  </span>
                  <div style={{ display: "inline-flex", alignItems: "center", columnGap: "20px" }}>
                    <label style={{ display: "inline-flex", alignItems: "center", columnGap: "6px", cursor: "pointer", fontSize: "0.9rem" }}>
                      <input
                        type="radio"
                        name="weitereTaetigkeitJaNein"
                        checked={!f("weitereTaetigkeit")}
                        onChange={() => weitereTaetigkeitSetzen("")}
                      />
                      <span>Nein</span>
                    </label>
                    <label style={{ display: "inline-flex", alignItems: "center", columnGap: "6px", cursor: "pointer", fontSize: "0.9rem" }}>
                      <input
                        type="radio"
                        name="weitereTaetigkeitJaNein"
                        checked={Boolean(f("weitereTaetigkeit"))}
                        onChange={() => {
                          if (!f("weitereTaetigkeit")) weitereTaetigkeitSetzen(WEITERE_TAETIGKEIT_STANDARD);
                        }}
                      />
                      <span>Ja</span>
                    </label>
                  </div>
                </div>

                {Boolean(f("weitereTaetigkeit")) && (
                  <FieldRow columns={2}>
                    <LabeledSelect
                      label="Weiterer Status"
                      name="weitereTaetigkeit"
                      value={f("weitereTaetigkeit")}
                      onChange={(v) => weitereTaetigkeitSetzen(v || WEITERE_TAETIGKEIT_STANDARD)}
                      options={WEITERE_TAETIGKEIT_OPTIONEN.map((o) => ({ value: o.value, label: o.label }))}
                    />
                    {berufSicht.arbeitgeber ? (
                      arbeitgeberAuswahl
                    ) : (
                      <LabeledInput
                        label="Tätigkeit"
                        name="position"
                        value={f("position")}
                        onChange={(v) => set("position", v)}
                        placeholder="z. B. IT-Beratung, Hausverwaltung, Gutachter …"
                      />
                    )}
                  </FieldRow>
                )}
              </div>
            )}

            {/* Schnellanlage Arbeitgeber darunter links (2 Spalten wie bei Vereinen) */}
            {berufSicht.arbeitgeber && arbeitgeberSchnellanlage}

            {/* Ist der Kontakt zusätzlich selbstständig? Nur wenn Angestellter oder Beamter */}
            {(f("occupationStatus") === "Angestellt" || f("occupationStatus") === "Beamter") && (
              <div style={{ display: "flex", flexDirection: "column", rowGap: "12px", marginTop: "6px" }}>
                <div style={{ display: "flex", flexDirection: "column", rowGap: "8px" }}>
                  <span style={{ fontSize: "0.88rem", fontWeight: 600, color: REFINED.ink }}>
                    Ist der Kontakt zusätzlich selbstständig?
                  </span>
                  <div style={{ display: "inline-flex", alignItems: "center", columnGap: "20px" }}>
                    <label style={{ display: "inline-flex", alignItems: "center", columnGap: "6px", cursor: "pointer", fontSize: "0.9rem" }}>
                      <input
                        type="radio"
                        name="zusaetzlichSelbststaendigJaNein"
                        checked={!f("zusaetzlichSelbststaendig")}
                        onChange={() => {
                          set("zusaetzlichSelbststaendig", "");
                          set("position", "");
                        }}
                      />
                      <span>Nein</span>
                    </label>
                    <label style={{ display: "inline-flex", alignItems: "center", columnGap: "6px", cursor: "pointer", fontSize: "0.9rem" }}>
                      <input
                        type="radio"
                        name="zusaetzlichSelbststaendigJaNein"
                        checked={Boolean(f("zusaetzlichSelbststaendig"))}
                        onChange={() => {
                          if (!f("zusaetzlichSelbststaendig")) {
                            set("zusaetzlichSelbststaendig", "neben");
                          }
                        }}
                      />
                      <span>Ja</span>
                    </label>
                  </div>
                </div>

                {Boolean(f("zusaetzlichSelbststaendig")) && (
                  <div style={{ display: "flex", flexDirection: "column", rowGap: "12px", paddingLeft: "4px" }}>
                    <div style={{ display: "inline-flex", alignItems: "center", columnGap: "20px" }}>
                      <label style={{ display: "inline-flex", alignItems: "center", columnGap: "6px", cursor: "pointer", fontSize: "0.9rem" }}>
                        <input
                          type="radio"
                          name="zusaetzlichSelbststaendigModus"
                          checked={f("zusaetzlichSelbststaendig") === "haupt"}
                          onChange={() => set("zusaetzlichSelbststaendig", "haupt")}
                        />
                        <span>Im Hauptberuf</span>
                      </label>
                      <label style={{ display: "inline-flex", alignItems: "center", columnGap: "6px", cursor: "pointer", fontSize: "0.9rem" }}>
                        <input
                          type="radio"
                          name="zusaetzlichSelbststaendigModus"
                          checked={f("zusaetzlichSelbststaendig") === "neben"}
                          onChange={() => set("zusaetzlichSelbststaendig", "neben")}
                        />
                        <span>Im Nebenberuf</span>
                      </label>
                    </div>

                    <FieldRow columns={2}>
                      <LabeledInput
                        label="Was macht er als Selbstständiger?"
                        name="position"
                        value={f("position")}
                        onChange={(v) => set("position", v)}
                        placeholder="z. B. IT-Beratung, Fotograf, Handwerker …"
                      />
                      <div />
                    </FieldRow>
                  </div>
                )}
              </div>
            )}

            {/* Tätigkeit als Selbstständiger, wenn Status Selbständig/Freiberufler */}
            {f("occupationStatus") === "Selbständig/Freiberufler" && (
              <div style={{ marginTop: "6px" }}>
                <FieldRow columns={2}>
                  <LabeledInput
                    label="Was macht er als Selbstständiger?"
                    name="position"
                    value={f("position")}
                    onChange={(v) => set("position", v)}
                    placeholder="z. B. IT-Beratung, Fotograf, Handwerker …"
                  />
                  <div />
                </FieldRow>
              </div>
            )}
          </SectionCard>
          )}

          {/* 7 — Vereine und Mitgliedschaften */}
          {!isApProduktpartner && (
            <SectionCard title="Vereine und Mitgliedschaften">
              <FieldRow columns={2}>
                <TagInput
                  label="Hobbys"
                  name="hobbys"
                  value={f("hobbys")}
                  onChange={(v) => set("hobbys", v)}
                  placeholder="Segeln, Oldtimer, Jagd, Golf …"
                />
                <TagInput
                  label="Vorlieben"
                  name="vorlieben"
                  value={f("vorlieben")}
                  onChange={(v) => set("vorlieben", v)}
                  placeholder="Wein, Klassik, Fußball …"
                />
              </FieldRow>
              <hr style={{ height: "1px", backgroundColor: REFINED.line, margin: "16px 0", borderWidth: 0 }} />
              <VereinMitgliedschaften
                value={vereine}
                onChange={onVereineChange}
              />
            </SectionCard>
          )}

          {/* Zuwendungen, Veranstaltungen & Newsletter */}
          {!isApProduktpartner && (
            <ZuwendungenKarte
              f={f}
              set={set}
              emailOptionen={newsletterEmailOptionen}
              uploadFile={uploadFileToAuthorities}
              onOpenDocument={(url) => {
                if (url) void oeffneAuthentifiziert(url.replace(/^\/+/, ""));
              }}
              onEinladungsDatei={(file) => pendingEinladungFilesRef.current.push(file)}
            />
          )}

          {/* Steuer & Sozialdaten — seit 09/2026 ein EIGENER Abschnitt.
              Vorher standen Steuerklasse, Jahreseinkommen und die
              Sozialversicherungsnummer im Beruf-Block; das Zielbild führt sie
              getrennt, weil sie nichts mit dem Arbeitgeber zu tun haben,
              sondern mit der Person. */}
          {!isApProduktpartner && (
          <SectionCard title="Steuer & Sozialdaten">
            {/* Gehaltsabrechnung scannen / hochladen */}
            <FieldRow columns={1}>
              <div>
                <button
                  type="button"
                  className={s.scanBtn}
                  title="Gehaltsabrechnung scannen oder hochladen"
                  onClick={() => setScanModus("gehalt")}
                >
                  <IconVisitenkarte /> Gehaltsabrechnung scannen / hochladen
                </button>
              </div>
            </FieldRow>
            {/* Ohne Schutzhinweis und ohne Rechteprüfung (PO 21.09.2026): Die
                Felder stehen jedem offen, der einen Kontakt anlegen darf. */}
            <div className={s.commCol}>
              <FieldRow columns={2}>
                <LabeledSelect
                  label="Steuerklasse"
                  name="taxClass"
                  value={f("taxClass")}
                  onChange={(v) => set("taxClass", v)}
                  options={STEUERKLASSEN.map((v) => ({ value: v, label: v }))}
                  placeholder="Steuerklasse auswählen"
                />
                <LabeledInput
                  label="Jahreseinkommen (€)"
                  name="annualIncome"
                  value={f("annualIncome")}
                  onChange={(v) => set("annualIncome", v)}
                  placeholder="84.000"
                />
              </FieldRow>
              {/* Steuer-ID und Steuernummer sind NICHT dasselbe: Die
                  Identifikationsnummer (§ 139b AO) ist lebenslang und
                  bundesweit eindeutig, die Steuernummer vergibt das
                  Finanzamt und sie ändert sich beim Umzug. Wer
                  selbstständig ist, hat beide. */}
              <FieldRow columns={2}>
                <LabeledInput
                  label="Steuer-ID"
                  name="taxIdNumber"
                  value={f("taxIdNumber")}
                  onChange={(v) => set("taxIdNumber", v)}
                  placeholder="12 345 678 901"
                />
                <LabeledInput
                  label="Steuernummer (bei Selbstständigkeit)"
                  name="taxNumber"
                  value={f("taxNumber")}
                  onChange={(v) => set("taxNumber", v)}
                  placeholder="201/123/12345"
                />
              </FieldRow>
              {/* Sozialversicherungs- und Rentenversicherungsnummer sind in
                  Deutschland DIESELBE zwölfstellige Nummer. Ein Feld, beide
                  Namen im Hinweis — zwei Felder würden dieselbe Nummer
                  zweimal erfassen lassen und beim Abgleich auseinanderlaufen. */}
              <LabeledInput
                label="Sozialversicherungsnummer"
                name="socialSecurityNumber"
                value={f("socialSecurityNumber")}
                onChange={(v) => set("socialSecurityNumber", v)}
                placeholder="65 170839 K 003"
              />
            </div>
          </SectionCard>
          )}

          {/* Vertretung und Bevollmächtigte */}
          {!isApProduktpartner && (
          <SectionCard title="Vertretung und Bevollmächtigte">
            <VertretungVollmachtenAbschnitt
              verfuegungen={verfuegungItems}
              onVerfuegungenChange={handleVerfuegungenChange}
              vertretungen={vertretungItems}
              onVertretungenChange={handleVertretungChange}
              uploadFile={uploadFileToAuthorities}
              onOpenDocument={(url) => {
                if (!url) return;
                if (url.startsWith("blob:") || url.startsWith("data:")) {
                  window.open(url, "_blank");
                } else {
                  void oeffneAuthentifiziert(url.replace(/^\/+/, ""));
                }
              }}
            />
          </SectionCard>
          )}

          {/* 8 — Bank (Zahlungsverkehr mit dem Kunden) */}
          {!isApProduktpartner && (
          <SectionCard title="Bank">
            <FieldRow columns={2}>
              <LabeledInput
                label="Kontoinhaber"
                name="accountHolder"
                value={f("accountHolder") || kontoinhaberName}
                onChange={(v) => set("accountHolder", v)}
                placeholder="Name des Kontoinhabers"
              />
              <IbanInput label="IBAN" name="iban" value={f("iban")} onChange={(v) => set("iban", v)} onBankErkannt={bankUebernehmen} />
            </FieldRow>
            <FieldRow columns={2}>
              <BicInput label="BIC" name="bic" value={f("bic")} onChange={(v) => set("bic", v)} />
              <LabeledInput label="Kreditinstitut" name="bankname" value={f("bankname")} onChange={(v) => set("bankname", v)} />
            </FieldRow>
            <FieldRow columns={2}>
              <LabeledInput label="Bezeichnung" name="bankbezeichnung" value={f("bankbezeichnung")} onChange={(v) => set("bankbezeichnung", v)} placeholder="z. B. Hauptkonto, Gehaltskonto" />
              <div />
            </FieldRow>
            <EintragsListe<WeitereBank>
              items={extraBanks}
              onChange={setzeWeitereBanken}
              neu={neueBank}
              hinzufuegenLabel="Weitere Bankverbindung"
              istLeer={bankIstLeer}
              pruefen={bankPruefen}
              renderFelder={(b, update, { index, entwurf }) => (
                <WeitereBankFelder wert={b} onChange={update} schluessel={entwurf ? "neu" : String(index)} kontoinhaberVorschlag={kontoinhaberName} />
              )}
            />
          </SectionCard>
          )}

          {/* 9 — Dokumente */}
          {!isApProduktpartner && (
          <SectionCard title="Dokumente">
            <AusweisdokumentAbschnitt
              documents={documents}
              onDocumentsChange={handleDocumentsChange}
              onScanClick={() => setScanModus("dokumente")}
              dateiName={dokDateien.name}
              onDateiEntfernen={dokDateien.entfernen}
              onOpenDocument={(url) => {
                if (url) void oeffneAuthentifiziert(url.replace(/^\/+/, ""));
              }}
            />
          </SectionCard>
          )}

          <SectionCard title="Social Media" collapsible defaultCollapsed={true}>
            <FieldRow columns={2}>
              <SocialMediaInput label="LinkedIn" name="linkedin" platform="linkedin" value={f("linkedin")} onChange={(v) => set("linkedin", v)} />
              <SocialMediaInput label="Instagram" name="instagram" platform="instagram" value={f("instagram")} onChange={(v) => set("instagram", v)} />
            </FieldRow>
            <FieldRow columns={2}>
              <SocialMediaInput label="Facebook" name="facebook" platform="facebook" value={f("facebook")} onChange={(v) => set("facebook", v)} />
              <SocialMediaInput label="TikTok" name="tiktok" platform="tiktok" value={f("tiktok")} onChange={(v) => set("tiktok", v)} />
            </FieldRow>
            <FieldRow columns={2}>
              <SocialMediaInput label="XING" name="xing" platform="xing" value={f("xing")} onChange={(v) => set("xing", v)} />
              <SocialMediaInput label="YouTube" name="youtube" platform="youtube" value={f("youtube")} onChange={(v) => set("youtube", v)} />
            </FieldRow>
          </SectionCard>

          {/* 9b — Bemerkung */}
          <SectionCard title="Bemerkung" collapsible defaultCollapsed={true}>
            <LabeledTextarea
              label="Bemerkung"
              name="remark"
              value={f("remark")}
              onChange={(v) => set("remark", v)}
              placeholder="Freie Notiz zu diesem Kontakt"
              rows={5}
              hint="Steht später in der Detailansicht."
            />
          </SectionCard>

          {/* 10 — Foto (im Bearbeiten-Modus im Kopf der Akte) */}
          {!bearbeiten && (
          <SectionCard title="Foto">
            <input id="priv-photo-input" type="file" accept="image/*" style={{ display: "none" }} onChange={onPhotoPick} />
            <button type="button" className={s.fileBtn} onClick={() => elementMitId("priv-photo-input")?.click()}>
              {photoName || "Foto hochladen"}
            </button>
          </SectionCard>
          )}

          {/* Verantwortlichkeit */}
          {!bearbeiten && (
            <SectionCard title="Verantwortlichkeit">
              <OwnershipSection formFields={formFields} updateField={updateField} />
            </SectionCard>
          )}

          {/* 11 — Verknüpfungen (inline: Haushalt/Personen, Verbindungen zu Firmen, KV).
              Beim Ansprechpartner tritt die Gesellschafts-Zuordnung an ihre
              Stelle — Haushalt und Kundenverbindung gibt es dort nicht. */}
          {!isApProduktpartner && (
            <PrivateRelationsSection
              formFields={formFields}
              updateField={updateField}
              selbstId={bearbeiten?.contactId}
              hochzeitDateien={hochzeitDateien}
              onAddHochzeitDateien={handleAddHochzeitDateien}
              onRemoveHochzeitDatei={handleRemoveHochzeitDatei}
            />
          )}

          {/* Vollmachten — ganz unten; die NKG-Karte für Privat folgt direkt
              danach (Parallel-Session). Beim Ansprechpartner eines
              Produktpartners entfällt sie. */}
          {!isApProduktpartner && !bearbeiten && (
            <VollmachtenKarte
              modus="anlage"
              value={vollmachten}
              onChange={setVollmachten}
              vorschauDaten={{
                anzeigeName: [f("lastname"), f("firstname")].map((x) => (x ?? "").trim()).filter(Boolean).join(", "),
                anschrift: anschriftEinzeilig(f("street"), f("postalcode"), f("city")),
              }}
              sammel={{
                haushalt: haushaltAusFormular(
                  parseArr(formFields.rel_household) as HouseholdItem[],
                  anschriftEinzeilig(f("street"), f("postalcode"), f("city")),
                ),
                verbund: verbundAusFormular([], parseArr(formFields.rel_personFirma) as PersonFirmaItem[]),
              }}
            />
          )}

          {/* 12 — NKG (nur bei Interessent), wie bei der Firma */}
          {!convert && !bearbeiten && isInteressent && <NkgKarte wert={nkgWanted} onChange={setNkgWanted} />}
        </fieldset>
      </div>

      {/* Im Bearbeiten-Modus der Akte speichert das Schloss im Aktenkopf —
          die Fußleiste entfällt (User 28.09.2026). */}
      {!bearbeiten && (
      <div className={s.footerBar}>
        {/* Ohne Pflichtfelder (Umwandlungs-Modus) hat die Fortschrittsleiste
            nichts anzuzeigen — dann bleibt sie weg statt 0/0 zu behaupten. */}
        {requiredChecks.length > 0 && (
          <RequiredFieldsProgress filled={requiredFilled} total={requiredChecks.length} />
        )}
        <div className={s.footer}>
          <button type="button" className={s.cancelBtn} onClick={onClose}>
            {bearbeiten ? "Änderungen verwerfen" : "Abbrechen"}
          </button>
          <AnlageSpeichernLeiste
            maengel={maengel}
            isSubmitting={isSubmitting}
            onSpeichern={speichern}
            // Den Analysemodus gibt es nur für Firmen — hier ausgegraut,
            // damit niemand ihn sucht (User 28.09.2026).
            analysemodusGesperrt={convert || bearbeiten ? undefined : "Nur für Firmen"}
            saveClassName={s.saveBtn}
            speichernText={speichernText}
          />
        </div>
      </div>
      )}
      <ConfirmDialog
        open={!!removalPrompt}
        tone="danger"
        title="Beziehungen entfernen?"
        confirmLabel="Umwandeln und entfernen"
        cancelLabel="Zurück zur Maske"
        busy={isSubmitting}
        message={
          <>
            <div>Diese Beziehungen werden beim Speichern gelöscht:</div>
            <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
              {(removalPrompt ?? []).map((label, i) => (
                <li key={i}>{label}</li>
              ))}
            </ul>
          </>
        }
        onConfirm={() => {
          setRemovalPrompt(null);
          void runSubmit();
        }}
        onCancel={() => setRemovalPrompt(null)}
      />
      <DateiScannenDialog
        open={scanModus !== null}
        ziel="privat"
        startArt={
          scanModus === "gehalt"
            ? "gehaltsabrechnung"
            : scanModus === "geburtsurkunde"
              ? "geburtsurkunde"
              : undefined
        }
        erlaubteArten={
          scanModus === "gehalt"
            ? ["gehaltsabrechnung"]
            : scanModus === "geburtsurkunde"
              ? ["geburtsurkunde"]
              : scanModus === "dokumente"
                ? ["personalausweis", "fuehrerschein", "geburtsurkunde", "heiratsurkunde", "sterbeurkunde"]
                : undefined
        }
        titel={scanModus === "dokumente" ? "Dokument scannen" : "Datei scannen"}
        kontakt={
          // T-41: In der Akte (Bearbeiten/Umwandeln) ist der Kontakt bekannt —
          // „Aus KMVS wählen" startet mit seinen Dokumenten. Fehlt der Name,
          // lädt die Auswahl ihn selbst nach.
          (bearbeiten?.contactId ?? convert?.contactId) != null
            ? {
                id: (bearbeiten?.contactId ?? convert?.contactId)!,
                name:
                  [str(formFields.lastname), str(formFields.firstname)].filter(Boolean).join(", ") ||
                  null,
              }
            : null
        }
        vorhanden={Object.fromEntries(
          Object.entries(formFields).map(([k, v]) => {
            // Dokumentfelder zählen nicht — das Ziel steht erst nach dem Lesen
            // fest, und `dokumentScanVerteilen` füllt dort ohnehin nur Leeres.
            if ((DOKUMENT_SCHLUESSEL as readonly string[]).includes(k)) {
              return [k, ""];
            }
            return [k, istVorgabewert(k, str(v)) ? "" : str(v)];
          }),
        )}
        onClose={() => {
          setScanModus(null);
        }}
        onUebernehmen={applyDateiScan}
      />
      <WeitereKommunikationDialog
        open={weitereKommOpen}
        onClose={() => setWeitereKommOpen(false)}
        defaultZuordnung="privat"
        availableCompanies={availableCompanies}
        checkExisting={checkExistingComm}
        vorhandeneWege={kontaktWege}
        eigeneKontaktId={eigeneKontaktId}
        onSave={(entry) => {
          addExtraComm(entry);
          setWeitereKommOpen(false);
        }}
        onAdd={(entry) => {
          addExtraComm(entry);
          setWeitereKommOpen(false);
        }}
      />
      <ErreichbarkeitModal
        open={erreichbarkeitModalOpen}
        onClose={() => setErreichbarkeitModalOpen(false)}
        allgemeinValue={allgemeineErreichbarkeit}
        onAllgemeinChange={handleAllgemeinChange}
        rufnummern={rufnummernFuerModal}
      />
      {befuellenArbeitgeberOffen && (
        <FirmendatenBefuellenDialog
          open={befuellenArbeitgeberOffen}
          eingabe={{
            name: String(arbeitgeberDraft.ref?.lastName ?? "").trim(),
            ort: null,
            plz: null,
            strasse: null,
            website: arbeitgeberDraft.ref?.web || null,
            email: arbeitgeberDraft.ref?.email || null,
          }}
          vorhanden={{
            name: String(arbeitgeberDraft.ref?.lastName ?? "").trim() || undefined,
            website: arbeitgeberDraft.ref?.web || undefined,
            email: arbeitgeberDraft.ref?.email || undefined,
            phone: arbeitgeberDraft.ref?.phone || undefined,
            rechtsform: arbeitgeberDraft.ref?.legalForm || undefined,
          }}
          onClose={() => setBefuellenArbeitgeberOffen(false)}
          // Logo-Vorschlag: gemerkt, übernommen wird es nach dem Anlegen des
          // Arbeitgebers (createContactFromRef → logoNachreichen).
          logo={{
            vorhanden: !!gemerktesLogo(arbeitgeberDraft.ref?.logoVorschlag),
            onJa: (bild) => {
              logoVergessen(arbeitgeberDraft.ref?.logoVorschlag);
              updateArbeitgeberDraft({
                isNew: true,
                ref: {
                  type: "Firma",
                  manual: true,
                  ...(arbeitgeberDraft.ref ?? {}),
                  logoVorschlag: logoMerken(bild),
                },
              });
            },
          }}
          onUebernehmen={(felder) => {
            const nextRef = {
              type: "Firma" as const,
              manual: true,
              ...(arbeitgeberDraft.ref ?? {}),
              ...(felder.name ? { lastName: felder.name } : {}),
              ...(felder.phone ? { phone: felder.phone } : {}),
              ...(felder.email ? { email: felder.email } : {}),
              ...(felder.website ? { web: felder.website } : {}),
              ...(felder.rechtsform ? { legalForm: felder.rechtsform } : {}),
            };
            updateArbeitgeberDraft({
              isNew: true,
              ref: nextRef,
            });
            if (felder.name) {
              set("employerContactName", felder.name);
            }
            setBefuellenArbeitgeberOffen(false);
          }}
        />
      )}
    </div>
    </KartenKlappContext.Provider>
    </VollanlageElternContext.Provider>
    </OffeneVollanlagenContext.Provider>
  );
};

export default NewPrivateContactForm;
